import React, { useEffect, useMemo, useState } from "react";
import ResimUpload from "../ResimUpload";

import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  alpha,
  keyframes,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import StorefrontIcon from "@mui/icons-material/Storefront";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import SettingsIcon from "@mui/icons-material/Settings";
import CategoryIcon from "@mui/icons-material/Category";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import ImageIcon from "@mui/icons-material/Image";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import StarIcon from "@mui/icons-material/Star";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import DiamondIcon from "@mui/icons-material/Diamond";
import VerifiedIcon from "@mui/icons-material/Verified";
import BoltIcon from "@mui/icons-material/Bolt";
import ComputerIcon from "@mui/icons-material/Computer";
import SpaIcon from "@mui/icons-material/Spa";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import CheckroomIcon from "@mui/icons-material/Checkroom";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import TuneIcon from "@mui/icons-material/Tune";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import GroupsIcon from "@mui/icons-material/Groups";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LanguageIcon from "@mui/icons-material/Language";
import CampaignIcon from "@mui/icons-material/Campaign";
import PaidIcon from "@mui/icons-material/Paid";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import PlaceIcon from "@mui/icons-material/Place";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import BlockIcon from "@mui/icons-material/Block";
import StoreMallDirectoryIcon from "@mui/icons-material/StoreMallDirectory";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const API_BASE =
  process.env.REACT_APP_API_BASE || "https://panel.haymestasarim.com/api";

const BRAND = {
  orange: "#F58220",
  orangeDark: "#C96713",
  orangeSoft: "#FFF3E6",
  blue: "#0B4D8B",
  blue2: "#1565C0",
  blueSoft: "#EAF4FF",
  dark: "#0F172A",
  ink: "#1E293B",
  muted: "#64748B",
  bg: "#F8FAFC",
  card: "#FFFFFF",
  line: "rgba(15,23,42,0.10)",
  success: "#10B981",
  danger: "#EF4444",
  warning: "#F59E0B",
  violet: "#7C3AED",
  pink: "#EC4899",
  green: "#22C55E",
};

const floatY = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-7px); }
  100% { transform: translateY(0px); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shine = keyframes`
  0% { transform: translateX(-140%); }
  100% { transform: translateX(140%); }
`;

/*
 * Kurumsal PC + mobil görünüm katmanı.
 * Yalnızca yerleşimi düzenler; API, state ve işlem akışına müdahale etmez.
 */
const MARKETPLACE_RESPONSIVE_CSS = `
  .myp-marketplace-page,
  .myp-marketplace-page * {
    box-sizing: border-box;
  }

  .myp-marketplace-page {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    overflow-x: clip;
  }

  .myp-marketplace-page .MuiBox-root,
  .myp-marketplace-page .MuiStack-root,
  .myp-marketplace-page .MuiCard-root,
  .myp-marketplace-page .MuiCardContent-root,
  .myp-marketplace-page .MuiFormControl-root {
    min-width: 0;
  }

  .myp-marketplace-page .MuiCard-root {
    max-width: 100%;
  }

  .myp-marketplace-page .MuiTypography-root,
  .myp-marketplace-page .MuiButton-root {
    overflow-wrap: anywhere;
  }

  .myp-marketplace-page .MuiButton-root {
    white-space: normal;
    text-align: center;
  }

  .myp-marketplace-page .MuiChip-root {
    max-width: 100%;
  }

  .myp-marketplace-page .MuiChip-label {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .myp-marketplace-content {
    width: 100%;
    max-width: none;
  }

  .myp-marketplace-header-inner,
  .myp-marketplace-category-strip,
  .myp-marketplace-content,
  .myp-marketplace-header-row,
  .myp-marketplace-header-search,
  .myp-hero-layout,
  .myp-stats-grid,
  .myp-shortcut-grid,
  .myp-products-grid,
  .myp-products-layout {
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  .myp-product-card {
    display: flex;
    flex-direction: column;
  }

  .myp-product-card > .MuiCardContent-root {
    display: flex;
    flex: 1;
  }

  .myp-product-card > .MuiCardContent-root > .MuiStack-root {
    width: 100%;
  }

  .myp-product-card > .MuiCardContent-root > .MuiStack-root > :last-child {
    margin-top: auto;
  }

  @media (min-width: 900px) {
    .myp-marketplace-header-row {
      display: grid !important;
      grid-template-columns: max-content minmax(240px, 1fr) max-content;
      align-items: center !important;
      gap: 16px !important;
    }

    .myp-marketplace-header-row > * {
      min-width: 0;
      margin: 0 !important;
    }

    .myp-marketplace-header-search {
      width: 100% !important;
      max-width: 100% !important;
    }

    .myp-marketplace-header-actions {
      width: auto;
      max-width: max-content;
      display: flex !important;
      flex-wrap: nowrap;
      flex: 0 0 auto;
    }

    .myp-hero-layout {
      display: grid !important;
      grid-template-columns: minmax(0, 1fr) max-content;
      align-items: center !important;
      gap: 20px !important;
    }

    .myp-hero-integrations {
      max-width: 460px;
      justify-content: flex-end;
    }
  }

  @media (max-width: 1199px) {
    .myp-marketplace-header {
      position: relative !important;
      top: auto !important;
    }
  }

  @media (max-width: 899px) {
    .myp-marketplace-header-inner,
    .myp-marketplace-content {
      padding-left: 12px !important;
      padding-right: 12px !important;
    }

    .myp-marketplace-header-actions {
      width: 100%;
      display: grid !important;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 8px !important;
    }

    .myp-marketplace-header-actions > .MuiButton-root {
      width: 100%;
      min-width: 0;
      margin: 0 !important;
    }

    .myp-marketplace-header-row {
      width: 100%;
      max-width: 100%;
      display: flex !important;
      flex-direction: column !important;
      align-items: stretch !important;
      gap: 12px !important;
    }

    .myp-marketplace-header-row > * {
      width: 100%;
      max-width: 100%;
      min-width: 0;
      margin: 0 !important;
    }

    .myp-marketplace-header-search {
      width: 100% !important;
      max-width: 100% !important;
    }

    .myp-hero-layout {
      width: 100%;
      max-width: 100%;
      display: flex !important;
      flex-direction: column !important;
      align-items: stretch !important;
      gap: 14px !important;
    }

    .myp-hero-integrations {
      width: 100%;
      max-width: 100%;
      justify-content: flex-start;
    }

    .myp-marketplace-category-strip {
      padding-left: 12px !important;
      padding-right: 12px !important;
      scroll-snap-type: x proximity;
    }

    .myp-marketplace-category-strip > * {
      scroll-snap-align: start;
    }
  }

  @media (max-width: 599px) {
    .myp-marketplace-header-inner,
    .myp-marketplace-content {
      padding-left: 7px !important;
      padding-right: 7px !important;
    }

    .myp-marketplace-header-inner {
      padding-top: 10px !important;
      padding-bottom: 10px !important;
    }

    .myp-marketplace-category-strip {
      padding-left: 7px !important;
      padding-right: 7px !important;
    }

    .myp-marketplace-page .MuiCard-root {
      border-radius: 16px;
    }

    .myp-marketplace-page .MuiCardContent-root {
      padding-left: 14px;
      padding-right: 14px;
    }

    .myp-marketplace-page .MuiInputBase-input,
    .myp-marketplace-page .MuiSelect-select,
    .myp-marketplace-dialog .MuiInputBase-input,
    .myp-marketplace-dialog .MuiSelect-select {
      font-size: 16px !important;
    }

    .myp-marketplace-page .MuiTabs-root {
      min-height: 52px;
    }

    .myp-marketplace-page .MuiTab-root {
      min-width: max-content;
      min-height: 52px !important;
      padding: 9px 12px;
      font-size: 12px;
    }

    .myp-marketplace-page .MuiIconButton-root {
      min-width: 40px;
      min-height: 40px;
    }

    .myp-marketplace-dialog .MuiDialog-container {
      align-items: stretch;
    }

    .myp-marketplace-dialog .MuiDialog-paper {
      width: 100%;
      max-width: 100%;
      height: 100dvh;
      max-height: 100dvh;
      margin: 0;
      border-radius: 0;
    }

    .myp-marketplace-dialog .MuiDialogTitle-root {
      padding: 15px 14px;
    }

    .myp-marketplace-dialog .MuiDialogContent-root {
      padding: 12px;
    }

    .myp-marketplace-dialog-actions {
      display: grid !important;
      grid-template-columns: minmax(0, 1fr);
      gap: 8px !important;
      padding: 10px 12px !important;
    }

    .myp-marketplace-dialog-actions > .MuiButton-root {
      width: 100%;
      min-height: 44px;
      margin: 0 !important;
    }

    .myp-marketplace-page .MuiFormControlLabel-root,
    .myp-marketplace-dialog .MuiFormControlLabel-root {
      width: 100%;
      margin-left: 0;
      margin-right: 0;
      align-items: flex-start;
    }

    .myp-stats-grid {
      grid-template-columns: minmax(0, 1fr) !important;
    }

    .myp-popular-title-row {
      width: 100%;
      align-items: flex-start !important;
      flex-wrap: wrap;
      gap: 8px;
    }

    .myp-hero-layout .MuiTypography-root {
      max-width: 100%;
      overflow-wrap: anywhere;
    }

    .myp-hero-integrations {
      display: grid !important;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 7px !important;
    }

    .myp-hero-integrations > .MuiChip-root {
      width: 100%;
      justify-content: flex-start;
      margin: 0 !important;
    }

    .myp-shortcut-grid,
    .myp-products-grid,
    .myp-products-layout {
      width: 100%;
      max-width: 100%;
      min-width: 0;
    }
  }

  @media (max-width: 380px) {
    .myp-shortcut-grid {
      grid-template-columns: minmax(0, 1fr) !important;
    }

    .myp-marketplace-header-actions,
    .myp-hero-integrations {
      grid-template-columns: minmax(0, 1fr) !important;
    }
  }
`;

const FLOW = {
  productTypes: ["physical", "vehicle", "realEstate", "service", "digital"],
  saleTypes: ["order", "lead", "catalog", "whatsapp"],
  statuses: ["active", "draft", "passive", "archived"],
  orderStatuses: [
    "new",
    "approved",
    "preparing",
    "shipped",
    "delivered",
    "cancelled",
  ],
  leadStatuses: [
    "pending",
    "new",
    "shared",
    "viewed",
    "contacted",
    "won",
    "lost",
    "rejected",
    "spam",
    "cancelled",
  ],
  currencies: ["TRY", "EUR", "CHF", "USD"],
};

const TEXT = {
  tr: {
    pageTitle: "MesYat Pazar Yeri",
    pageSubtitle:
      "Ürün ve ilanlarınızı yayınlayın; MesYat alıcıyla satıcıyı güvenli iletişim akışında buluştursun.",
    search: "Ürün, kategori, marka veya ilan ara",
    categories: "Kategoriler",
    addProduct: "Ürün / İlan Ekle",
    addCategory: "Kategori Ekle",
    refresh: "Yenile",
    showcase: "Vitrin",
    products: "Ürünler / İlanlar",
    orders: "Eski Siparişler",
    leads: "Pazar Yeri Talepleri",
    settings: "Ayarlar",
    filters: "Filtreler",
    clearFilters: "Filtreleri Temizle",
    all: "Tümü",
    category: "Kategori",
    productType: "Ürün Tipi",
    saleType: "Satış Akışı",
    status: "Durum",
    sort: "Sıralama",
    popular: "Popüler",
    newest: "Yeni",
    priceAsc: "Fiyat Artan",
    priceDesc: "Fiyat Azalan",
    totalProducts: "Toplam Ürün",
    activeProducts: "Aktif Ürün",
    pendingOrders: "Satıcıya İletilen",
    newLeads: "Onay Bekleyen",
    whatSell: "Ne pazarlamak istiyorsun?",
    whatSellDesc:
      "Seç, alanlar otomatik hazırlansın. Araç/emlak teklif akışı; ürünler sepet/sipariş akışı.",
    heroTitle: "Kim ne istiyorsa burada pazarlasın",
    heroDesc:
      "Letgo benzeri ilan modeli: MesYat ödeme almaz; alıcı ve satıcıyı talep, bildirim ve işletme profili üzerinden buluşturur.",
    popularProducts: "Popüler Ürünler",
    seeAll: "Tümünü Gör",
    noProduct: "Henüz ürün yok",
    noProductDesc: "İlk ürünü ekle, vitrin burada dolmaya başlasın.",
    noFiltered: "Filtreye uygun ürün yok",
    noFilteredDesc: "Filtreleri temizle veya yeni ürün ekle.",
    noOrders: "Henüz sipariş yok",
    noOrdersDesc: "Sepet/sipariş akışındaki ürünlerden sipariş geldiğinde burada görünür.",
    noLeads: "Henüz pazar yeri talebi yok",
    noLeadsDesc:
      "Alıcıların ürün ve ilanlar için gönderdiği talepler burada görünür.",
    productAdd: "Ürün / İlan Ekle",
    productEdit: "Ürün / İlan Düzenle",
    productAddDesc: "MesYat pazar yeri vitrini için ürün bilgilerini doldur.",
    selectSellType: "Ne satıyorsun?",
    infoHint:
      "Araç ve emlak için teklif akışı önerilir. Butik, iç giyim, elektronik ve kozmetik ürünlerde sepet/sipariş akışı kullanılır.",
    basicInfo: "Temel Ürün Bilgileri",
    priceInfo: "Kategori, Satış Akışı ve Fiyat",
    imageInfo: "Ürün Görselleri",
    stockInfo: "Varyant, Stok ve Etiketler",
    nameTr: "Ürün Adı TR",
    nameEn: "Ürün Adı EN",
    nameDe: "Ürün Adı DE",
    descTr: "Açıklama TR",
    descEn: "Description EN",
    descDe: "Beschreibung DE",
    brand: "Marka",
    model: "Model",
    sku: "Stok / İlan Kodu",
    price: "Fiyat",
    oldPrice: "Eski Fiyat",
    currency: "Para Birimi",
    vat: "KDV %",
    stockQty: "Stok Adedi",
    lowStock: "Kritik Stok",
    sizes: "Bedenler / Seçenekler",
    colors: "Renkler",
    tags: "Etiketler",
    note: "İç Not",
    save: "Kaydet",
    update: "Güncelle",
    cancel: "Vazgeç",
    close: "Kapat",
    active: "Aktif",
    draft: "Taslak",
    passive: "Pasif",
    archived: "Arşiv",
    stockEnabled: "Stok takibi aktif",
    featured: "Öne çıkan",
    campaign: "Kampanyalı",
    newBadge: "Yeni ürün",
    whatsappActive: "WhatsApp aktif",
    stockModule: "Stok modülü",
    cashModule: "Kasa modülü",
    cariModule: "Cari modülü",
    requiredName: "Türkçe ürün adı zorunlu.",
    requiredCategory: "Kategori seçmelisin.",
    requiredPrice: "Fiyat sıfırdan büyük olmalı.",
    productSaved: "Ürün kaydedildi.",
    productUpdated: "Ürün güncellendi.",
    productDeleted: "Ürün pasifleştirildi.",
    categorySaved: "Kategori kaydedildi.",
    settingsSaved: "Mağaza ayarları kaydedildi.",
    detailPreview: "Ürün Detay Önizleme",
    nowBuy: "Şimdi Al",
    addBasket: "Sepete Ekle",
    requestOffer: "Teklif / Bilgi Al",
    whatsappBuy: "WhatsApp ile Al",
    storeInfo: "Mağaza Bilgisi",
    campaigns: "Ürünün Kampanyaları",
    description: "Açıklama",
    deliveryInfo: "Teslimat / kargo bilgisi mağaza ayarlarından yönetilir.",
    storeSettings: "Mağaza Ayarları",
    storeName: "Mağaza Adı",
    storeSlug: "Mağaza Kısa Link",
    whatsappNumber: "WhatsApp Numarası",
    defaultCurrency: "Varsayılan Para Birimi",
    minOrder: "Minimum Sipariş Tutarı",
    logoUrl: "Logo URL",
    storeDesc: "Mağaza Açıklaması TR",
    delivery: "Teslimat / Kargo Bilgisi TR",
    returnPolicy: "İade / Değişim Bilgisi TR",
    saveSettings: "Ayarları Kaydet",
    categoryAdd: "Kategori Ekle",
    categoryNameTr: "Kategori Adı TR",
    categoryNameEn: "Category EN",
    categoryNameDe: "Kategorie DE",
    slug: "Slug",
    icon: "İkon",
    color: "Renk",
    other: "Diğer",
    physical: "Fiziksel Ürün",
    vehicle: "Araç / Galeri",
    realEstate: "Emlak / Kiralık",
    service: "Hizmet / Paket",
    digital: "Dijital Ürün",
    orderSale: "Talep / İletişim",
    leadSale: "Teklif / Bilgi Al",
    catalogSale: "Sadece Vitrin",
    whatsappSale: "WhatsApp İletişim",
  },
  en: {
    pageTitle: "MesYat Marketplace",
    pageSubtitle:
      "Manage vehicles, real estate, fashion, electronics, cosmetics, services, digital products and all other listings in one professional storefront.",
    search: "Search product, category, brand or listing",
    categories: "Categories",
    addProduct: "Add Product / Listing",
    addCategory: "Add Category",
    refresh: "Refresh",
    showcase: "Showcase",
    products: "Products / Listings",
    orders: "Orders",
    leads: "Lead Requests",
    settings: "Settings",
    filters: "Filters",
    clearFilters: "Clear Filters",
    all: "All",
    category: "Category",
    productType: "Product Type",
    saleType: "Sales Flow",
    status: "Status",
    sort: "Sort",
    popular: "Popular",
    newest: "Newest",
    priceAsc: "Price Low to High",
    priceDesc: "Price High to Low",
    totalProducts: "Total Products",
    activeProducts: "Active Products",
    pendingOrders: "Pending Orders",
    newLeads: "New Leads",
    whatSell: "What do you want to sell?",
    whatSellDesc:
      "Choose a type and fields are prepared automatically. Vehicles/real estate use lead flow; retail products use cart/order flow.",
    heroTitle: "Sell anything professionally",
    heroDesc:
      "A professional marketplace with MesYat colors: storefront, gallery images, orders, leads, stock, cash and customer account readiness.",
    popularProducts: "Popular Products",
    seeAll: "See All",
    noProduct: "No products yet",
    noProductDesc: "Add your first product and your showcase will start here.",
    noFiltered: "No matching products",
    noFilteredDesc: "Clear filters or add a new product.",
    noOrders: "No orders yet",
    noOrdersDesc: "Orders from cart/order products will appear here.",
    noLeads: "No leads yet",
    noLeadsDesc: "Lead requests from vehicles, real estate or lead products will appear here.",
    productAdd: "Add Product / Listing",
    productEdit: "Edit Product / Listing",
    productAddDesc: "Fill in product details for the MesYat marketplace storefront.",
    selectSellType: "What are you selling?",
    infoHint:
      "Lead flow is recommended for vehicles and real estate. Cart/order flow is used for fashion, electronics and cosmetics.",
    basicInfo: "Basic Product Information",
    priceInfo: "Category, Sales Flow and Price",
    imageInfo: "Product Images",
    stockInfo: "Variants, Stock and Tags",
    nameTr: "Product Name TR",
    nameEn: "Product Name EN",
    nameDe: "Product Name DE",
    descTr: "Description TR",
    descEn: "Description EN",
    descDe: "Description DE",
    brand: "Brand",
    model: "Model",
    sku: "SKU / Listing Code",
    price: "Price",
    oldPrice: "Old Price",
    currency: "Currency",
    vat: "VAT %",
    stockQty: "Stock Qty",
    lowStock: "Critical Stock",
    sizes: "Sizes / Options",
    colors: "Colors",
    tags: "Tags",
    note: "Internal Note",
    save: "Save",
    update: "Update",
    cancel: "Cancel",
    close: "Close",
    active: "Active",
    draft: "Draft",
    passive: "Passive",
    archived: "Archived",
    stockEnabled: "Stock tracking active",
    featured: "Featured",
    campaign: "Campaign",
    newBadge: "New product",
    whatsappActive: "WhatsApp active",
    stockModule: "Stock module",
    cashModule: "Cash module",
    cariModule: "Customer module",
    requiredName: "Turkish product name is required.",
    requiredCategory: "Please select a category.",
    requiredPrice: "Price must be greater than zero.",
    productSaved: "Product saved.",
    productUpdated: "Product updated.",
    productDeleted: "Product deactivated.",
    categorySaved: "Category saved.",
    settingsSaved: "Store settings saved.",
    detailPreview: "Product Detail Preview",
    nowBuy: "Buy Now",
    addBasket: "Add to Cart",
    requestOffer: "Request Info / Offer",
    whatsappBuy: "Buy via WhatsApp",
    storeInfo: "Store Info",
    campaigns: "Product Campaigns",
    description: "Description",
    deliveryInfo: "Delivery / shipping information is managed from store settings.",
    storeSettings: "Store Settings",
    storeName: "Store Name",
    storeSlug: "Store Short Link",
    whatsappNumber: "WhatsApp Number",
    defaultCurrency: "Default Currency",
    minOrder: "Minimum Order Amount",
    logoUrl: "Logo URL",
    storeDesc: "Store Description TR",
    delivery: "Delivery / Shipping Info TR",
    returnPolicy: "Return / Exchange Info TR",
    saveSettings: "Save Settings",
    categoryAdd: "Add Category",
    categoryNameTr: "Category Name TR",
    categoryNameEn: "Category EN",
    categoryNameDe: "Category DE",
    slug: "Slug",
    icon: "Icon",
    color: "Color",
    other: "Other",
    physical: "Physical Product",
    vehicle: "Vehicle / Gallery",
    realEstate: "Real Estate / Rental",
    service: "Service / Package",
    digital: "Digital Product",
    orderSale: "Cart / Order",
    leadSale: "Lead / Get Info",
    catalogSale: "Catalog Only",
    whatsappSale: "WhatsApp Order",
  },
  de: {
    pageTitle: "MesYat Marktplatz",
    pageSubtitle:
      "Fahrzeuge, Immobilien, Mode, Elektronik, Kosmetik, Dienstleistungen, digitale Produkte und alle anderen Anzeigen professionell verwalten.",
    search: "Produkt, Kategorie, Marke oder Anzeige suchen",
    categories: "Kategorien",
    addProduct: "Produkt / Anzeige hinzufügen",
    addCategory: "Kategorie hinzufügen",
    refresh: "Aktualisieren",
    showcase: "Schaufenster",
    products: "Produkte / Anzeigen",
    orders: "Bestellungen",
    leads: "Anfragen",
    settings: "Einstellungen",
    filters: "Filter",
    clearFilters: "Filter löschen",
    all: "Alle",
    category: "Kategorie",
    productType: "Produkttyp",
    saleType: "Verkaufsablauf",
    status: "Status",
    sort: "Sortieren",
    popular: "Beliebt",
    newest: "Neu",
    priceAsc: "Preis aufsteigend",
    priceDesc: "Preis absteigend",
    totalProducts: "Produkte gesamt",
    activeProducts: "Aktive Produkte",
    pendingOrders: "Offene Bestellungen",
    newLeads: "Neue Anfragen",
    whatSell: "Was möchten Sie verkaufen?",
    whatSellDesc:
      "Typ auswählen, Felder werden automatisch vorbereitet. Fahrzeuge/Immobilien nutzen Anfragefluss; Produkte nutzen Warenkorb/Bestellung.",
    heroTitle: "Alles professionell verkaufen",
    heroDesc:
      "Professioneller Marktplatz in MesYat-Farben: Schaufenster, Galerie, Bestellungen, Anfragen, Lager, Kasse und Kundenkonto bereit.",
    popularProducts: "Beliebte Produkte",
    seeAll: "Alle anzeigen",
    noProduct: "Noch keine Produkte",
    noProductDesc: "Erstes Produkt hinzufügen und Schaufenster starten.",
    noFiltered: "Keine passenden Produkte",
    noFilteredDesc: "Filter löschen oder neues Produkt hinzufügen.",
    noOrders: "Noch keine Bestellungen",
    noOrdersDesc: "Bestellungen aus Warenkorb-Produkten erscheinen hier.",
    noLeads: "Noch keine Anfragen",
    noLeadsDesc: "Anfragen aus Fahrzeugen, Immobilien oder Anfrage-Produkten erscheinen hier.",
    productAdd: "Produkt / Anzeige hinzufügen",
    productEdit: "Produkt / Anzeige bearbeiten",
    productAddDesc: "Produktdetails für den MesYat-Marktplatz ausfüllen.",
    selectSellType: "Was verkaufen Sie?",
    infoHint:
      "Für Fahrzeuge und Immobilien wird Anfragefluss empfohlen. Für Mode, Elektronik und Kosmetik Warenkorb/Bestellung.",
    basicInfo: "Grundinformationen",
    priceInfo: "Kategorie, Verkaufsablauf und Preis",
    imageInfo: "Produktbilder",
    stockInfo: "Varianten, Lager und Tags",
    nameTr: "Produktname TR",
    nameEn: "Produktname EN",
    nameDe: "Produktname DE",
    descTr: "Beschreibung TR",
    descEn: "Beschreibung EN",
    descDe: "Beschreibung DE",
    brand: "Marke",
    model: "Modell",
    sku: "SKU / Anzeigen-Code",
    price: "Preis",
    oldPrice: "Alter Preis",
    currency: "Währung",
    vat: "MwSt. %",
    stockQty: "Lagerbestand",
    lowStock: "Kritischer Bestand",
    sizes: "Größen / Optionen",
    colors: "Farben",
    tags: "Tags",
    note: "Interne Notiz",
    save: "Speichern",
    update: "Aktualisieren",
    cancel: "Abbrechen",
    close: "Schließen",
    active: "Aktiv",
    draft: "Entwurf",
    passive: "Passiv",
    archived: "Archiv",
    stockEnabled: "Lagerverfolgung aktiv",
    featured: "Empfohlen",
    campaign: "Aktion",
    newBadge: "Neues Produkt",
    whatsappActive: "WhatsApp aktiv",
    stockModule: "Lagermodul",
    cashModule: "Kassenmodul",
    cariModule: "Kundenmodul",
    requiredName: "Türkischer Produktname ist erforderlich.",
    requiredCategory: "Kategorie auswählen.",
    requiredPrice: "Preis muss größer als null sein.",
    productSaved: "Produkt gespeichert.",
    productUpdated: "Produkt aktualisiert.",
    productDeleted: "Produkt deaktiviert.",
    categorySaved: "Kategorie gespeichert.",
    settingsSaved: "Shop-Einstellungen gespeichert.",
    detailPreview: "Produktdetail Vorschau",
    nowBuy: "Jetzt kaufen",
    addBasket: "In den Warenkorb",
    requestOffer: "Anfrage / Info",
    whatsappBuy: "Über WhatsApp kaufen",
    storeInfo: "Shop Info",
    campaigns: "Produktaktionen",
    description: "Beschreibung",
    deliveryInfo: "Liefer- und Versandinformationen werden in den Shop-Einstellungen verwaltet.",
    storeSettings: "Shop Einstellungen",
    storeName: "Shop Name",
    storeSlug: "Shop Kurzlink",
    whatsappNumber: "WhatsApp Nummer",
    defaultCurrency: "Standardwährung",
    minOrder: "Mindestbestellwert",
    logoUrl: "Logo URL",
    storeDesc: "Shop Beschreibung TR",
    delivery: "Liefer- / Versandinfo TR",
    returnPolicy: "Rückgabe / Umtauschinfo TR",
    saveSettings: "Einstellungen speichern",
    categoryAdd: "Kategorie hinzufügen",
    categoryNameTr: "Kategorie Name TR",
    categoryNameEn: "Kategorie EN",
    categoryNameDe: "Kategorie DE",
    slug: "Slug",
    icon: "Icon",
    color: "Farbe",
    other: "Andere",
    physical: "Physisches Produkt",
    vehicle: "Fahrzeug / Galerie",
    realEstate: "Immobilie / Miete",
    service: "Service / Paket",
    digital: "Digitales Produkt",
    orderSale: "Warenkorb / Bestellung",
    leadSale: "Anfrage / Info",
    catalogSale: "Nur Katalog",
    whatsappSale: "WhatsApp Bestellung",
  },
};

const emptyProduct = {
  id: null,
  category_id: "",
  name: { tr: "", en: "", de: "" },
  description: { tr: "", en: "", de: "" },
  product_type: "physical",
  sale_type: "order",
  status: "active",
  brand: "",
  model: "",
  sku: "",
  price: "",
  compare_price: "",
  currency: "TRY",
  vat_rate: "20",
  stock_enabled: true,
  stock_qty: "10",
  low_stock: "3",
  sizesText: "",
  colorsText: "",
  tagsText: "",
  cover_image: "",
  imagesText: "",
  note: "",
  featured: false,
  campaign: false,
  new_badge: true,
  whatsapp_order: false,
  integrate_stock: true,
  integrate_cash: true,
  integrate_cari: true,
  views: 0,
  sales_count: 0,
};

const emptyCategory = {
  id: null,
  name: { tr: "", en: "", de: "" },
  slug: "",
  icon: "category",
  color: BRAND.orange,
  active: true,
};

const defaultSettings = {
  store_name: "MesYat Dijital Mağaza",
  store_slug: "mesyat-magaza",
  store_description: {
    tr: "Ürün, ilan, kampanya ve teklif taleplerinizi tek yerden yönetin.",
    en: "Manage products, listings, campaigns and leads from one place.",
    de: "Produkte, Anzeigen, Aktionen und Anfragen zentral verwalten.",
  },
  delivery_info: {
    tr: "Teslimat ve ödeme koşulları alıcı ile satıcı arasında belirlenir.",
    en: "Delivery information is provided by the business.",
    de: "Lieferinformationen werden vom Betrieb bereitgestellt.",
  },
  return_policy: {
    tr: "MesYat ödemeye ve satış sözleşmesine taraf değildir. İade koşulları satıcı işletme tarafından belirlenir.",
    en: "Return terms depend on business policies.",
    de: "Rückgabe gemäß Geschäftsbedingungen.",
  },
  store_active: true,
  order_active: false,
  lead_active: true,
  min_order_amount: 0,
  default_currency: "TRY",
  whatsapp_number: "905336437615",
  cover_url: "",
  logo_url: "",
  require_approval: false,
  show_stock_on_flutter: false,
  allow_guest_order: true,
  auto_cash_record: false,
  auto_cari_record: false,
  auto_stock_decrease: false,
};

const fallbackCategories = [
  {
    id: "cat_butik",
    name: { tr: "Butik & İç Giyim", en: "Boutique & Fashion", de: "Boutique & Mode" },
    slug: "butik-ic-giyim",
    icon: "fashion",
    color: BRAND.pink,
    active: true,
  },
  {
    id: "cat_arac",
    name: { tr: "Araç & Galeri", en: "Vehicle & Gallery", de: "Fahrzeug & Galerie" },
    slug: "arac-galeri",
    icon: "car",
    color: BRAND.blue,
    active: true,
  },
  {
    id: "cat_emlak",
    name: { tr: "Emlak", en: "Real Estate", de: "Immobilien" },
    slug: "emlak",
    icon: "home",
    color: BRAND.green,
    active: true,
  },
  {
    id: "cat_elektronik",
    name: { tr: "Elektronik", en: "Electronics", de: "Elektronik" },
    slug: "elektronik",
    icon: "computer",
    color: BRAND.blue2,
    active: true,
  },
  {
    id: "cat_kozmetik",
    name: { tr: "Kozmetik", en: "Cosmetics", de: "Kosmetik" },
    slug: "kozmetik",
    icon: "cosmetic",
    color: BRAND.violet,
    active: true,
  },
  {
    id: "cat_gida",
    name: { tr: "Gıda & Market", en: "Food & Market", de: "Lebensmittel & Markt" },
    slug: "gida-market",
    icon: "food",
    color: BRAND.warning,
    active: true,
  },
  {
    id: "cat_hizmet",
    name: { tr: "Hizmet & Paket", en: "Service & Package", de: "Service & Paket" },
    slug: "hizmet-paket",
    icon: "service",
    color: BRAND.orange,
    active: true,
  },
  {
    id: "cat_dijital",
    name: { tr: "Dijital Ürün", en: "Digital Product", de: "Digitales Produkt" },
    slug: "dijital-urun",
    icon: "digital",
    color: BRAND.dark,
    active: true,
  },
  {
    id: "cat_diger",
    name: { tr: "Diğer", en: "Other", de: "Andere" },
    slug: "diger",
    icon: "other",
    color: BRAND.muted,
    active: true,
  },
];

function getLang() {
  const raw =
    localStorage.getItem("panel_dili") ||
    localStorage.getItem("app_lang") ||
    localStorage.getItem("lang") ||
    "tr";

  const lang = String(raw).slice(0, 2).toLowerCase();
  return ["tr", "en", "de"].includes(lang) ? lang : "tr";
}

function getCurrentUserEmail() {
  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("accessToken") ||
    "";

  if (!token) return "";

  try {
    const tokenPart = token.split(".")[1];
    if (!tokenPart) return "";

    const base64 = tokenPart
      .replace(/-/g, "+")
      .replace(/_/g, "/");

    const padded = base64.padEnd(
      Math.ceil(base64.length / 4) * 4,
      "="
    );

    const payload = JSON.parse(window.atob(padded));

    return String(
      payload.email ||
      payload.mail ||
      payload.user_email ||
      payload.eposta ||
      ""
    )
      .trim()
      .toLowerCase();
  } catch {
    return "";
  }
}

function tokenHeader() {
  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("accessToken") ||
    "";

  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function marketplaceApi(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      ...tokenHeader(),
      ...(options.headers || {}),
    },
  });

  const text = await res.text();

  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { ok: false, message: text };
  }

  if (!res.ok || data.ok === false) {
    throw new Error(data.message || "Marketplace API hatası");
  }

  return data;
}

function safeJson(value, fallback) {
  if (value === undefined || value === null) return fallback;
  if (typeof value === "object") return value;

  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function safeArray(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed;
    } catch {}

    return value
      .split(/\n|,/)
      .map((x) => x.trim())
      .filter(Boolean);
  }

  return [];
}

function textList(value) {
  return safeArray(value).join("\n");
}

function toNumber(value) {
  const n = Number(String(value ?? 0).replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

function localized(value, lang = "tr") {
  if (!value) return "";
  if (typeof value === "string") return value;

  return value[lang] || value.tr || value.en || value.de || "";
}

function normalizeSearch(value) {
  return String(value || "")
    .toLowerCase()
    .replaceAll("ı", "i")
    .replaceAll("ğ", "g")
    .replaceAll("ü", "u")
    .replaceAll("ş", "s")
    .replaceAll("ö", "o")
    .replaceAll("ç", "c");
}

function slugify(value) {
  return normalizeSearch(value)
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function money(value, currency = "TRY", lang = "tr") {
  const locale = lang === "de" ? "de-DE" : lang === "en" ? "en-US" : "tr-TR";

  return `${toNumber(value).toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${currency || ""}`;
}

function percentDiscount(product) {
  const oldPrice = toNumber(product.compare_price);
  const price = toNumber(product.price);

  if (!oldPrice || !price || oldPrice <= price) return 0;

  return Math.round(((oldPrice - price) / oldPrice) * 100);
}

function categoryIcon(icon) {
  if (icon === "car") return <DirectionsCarIcon />;
  if (icon === "home") return <HomeWorkIcon />;
  if (icon === "fashion" || icon === "diamond") return <DiamondIcon />;
  if (icon === "computer") return <ComputerIcon />;
  if (icon === "cosmetic") return <SpaIcon />;
  if (icon === "food") return <RestaurantIcon />;
  if (icon === "service") return <VerifiedIcon />;
  if (icon === "digital") return <BoltIcon />;
  if (icon === "other") return <MoreHorizIcon />;
  return <CategoryIcon />;
}

function productTypeIcon(type) {
  if (type === "vehicle") return <DirectionsCarIcon />;
  if (type === "realEstate") return <HomeWorkIcon />;
  if (type === "service") return <VerifiedIcon />;
  if (type === "digital") return <BoltIcon />;
  return <Inventory2Icon />;
}

function productTypeLabel(TT, type) {
  if (type === "physical") return TT.physical;
  if (type === "vehicle") return TT.vehicle;
  if (type === "realEstate") return TT.realEstate;
  if (type === "service") return TT.service;
  if (type === "digital") return TT.digital;
  return type;
}

function saleTypeLabel(TT, type) {
  if (type === "order") return TT.orderSale;
  if (type === "lead") return TT.leadSale;
  if (type === "catalog") return TT.catalogSale;
  if (type === "whatsapp") return TT.whatsappSale;
  return type;
}

function statusLabel(TT, status) {
  if (status === "active") return TT.active;
  if (status === "draft") return TT.draft;
  if (status === "passive") return TT.passive;
  if (status === "archived") return TT.archived;
  return status;
}


function approvalStatusLabel(status, lang = "tr") {
  const labels = {
    tr: {
      pending: "Yönetici Onayı Bekliyor",
      approved: "Yönetici Tarafından Onaylandı",
      rejected: "Yönetici Tarafından Reddedildi",
    },
    en: {
      pending: "Waiting for Admin Approval",
      approved: "Approved by Admin",
      rejected: "Rejected by Admin",
    },
    de: {
      pending: "Wartet auf Administratorfreigabe",
      approved: "Vom Administrator freigegeben",
      rejected: "Vom Administrator abgelehnt",
    },
  };

  return labels[lang]?.[status] || status;
}


function orderStatusLabel(status, lang = "tr") {
  const labels = {
    tr: {
      new: "Yeni Sipariş",
      approved: "Onaylandı",
      preparing: "Hazırlanıyor",
      shipped: "Kargoda",
      delivered: "Teslim Edildi",
      cancelled: "İptal",
    },
    en: {
      new: "New Order",
      approved: "Approved",
      preparing: "Preparing",
      shipped: "Shipped",
      delivered: "Delivered",
      cancelled: "Cancelled",
    },
    de: {
      new: "Neue Bestellung",
      approved: "Bestätigt",
      preparing: "In Vorbereitung",
      shipped: "Versendet",
      delivered: "Geliefert",
      cancelled: "Storniert",
    },
  };

  return labels[lang]?.[status] || status;
}

function leadStatusLabel(status, lang = "tr") {
  const labels = {
    tr: {
      pending: "Yönetici Onayı Bekliyor",
      new: "Yeni Talep",
      approved: "Onaylandı",
      shared: "Satıcıya İletildi",
      viewed: "Satıcı Görüntüledi",
      contacted: "İletişime Geçildi",
      offerSent: "Teklif Gönderildi",
      won: "Satışa Dönüştü",
      lost: "Satış Olmadı",
      rejected: "Reddedildi",
      spam: "Spam",
      cancelled: "Alıcı İptal Etti",
    },
    en: {
      pending: "Waiting for Approval",
      new: "New Request",
      approved: "Approved",
      shared: "Sent to Seller",
      viewed: "Viewed by Seller",
      contacted: "Contacted",
      offerSent: "Offer Sent",
      won: "Converted",
      lost: "Not Converted",
      rejected: "Rejected",
      spam: "Spam",
      cancelled: "Cancelled",
    },
    de: {
      pending: "Wartet auf Freigabe",
      new: "Neue Anfrage",
      approved: "Freigegeben",
      shared: "An Verkäufer gesendet",
      viewed: "Vom Verkäufer angesehen",
      contacted: "Kontaktiert",
      offerSent: "Angebot gesendet",
      won: "Erfolgreich",
      lost: "Nicht erfolgreich",
      rejected: "Abgelehnt",
      spam: "Spam",
      cancelled: "Storniert",
    },
  };

  return labels[lang]?.[status] || status;
}

function statusColor(status) {
  if (["active", "delivered", "won", "approved", "shared", "viewed"].includes(status)) {
    return "success";
  }
  if (["pending", "new", "preparing", "contacted", "offerSent", "shipped", "draft"].includes(status)) {
    return "warning";
  }
  if (["passive", "cancelled", "lost", "archived", "rejected", "spam"].includes(status)) {
    return "error";
  }
  return "default";
}

function mapCategory(row) {
  return {
    ...row,
    name: safeJson(row.name, row.name || { tr: "" }),
    active: row.active !== false,
  };
}

function categoryIdentity(category) {
  const nameTr = normalizeSearch(localized(category?.name, "tr"));
  const nameEn = normalizeSearch(localized(category?.name, "en"));
  const nameDe = normalizeSearch(localized(category?.name, "de"));
  const slug = normalizeSearch(category?.slug || "");
  const all = `${nameTr} ${nameEn} ${nameDe} ${slug}`;

  // Aynı iş kolunun farklı yazılmış karşılıklarını tek vitrinde göster.
  if (
    /(butik|boutique|fashion|mode)/.test(all) &&
    /(giyim|fashion|mode|bekleidung)/.test(all)
  ) {
    return "butik-giyim";
  }

  const canonicalGroups = [
    ["elektronik", /(elektronik|electronics|electronic)/],
    ["arac-galeri", /(arac|galeri|vehicle|fahrzeug)/],
    ["emlak", /(emlak|real estate|immobil)/],
    ["kozmetik", /(kozmetik|cosmetic|kosmetik)/],
    ["gida-market", /(gida|market|food|lebensmittel)/],
    ["hizmet-paket", /(hizmet|service|paket|package)/],
    ["dijital-urun", /(dijital|digital)/],
  ];

  const matched = canonicalGroups.find(([, pattern]) => pattern.test(all));
  if (matched) return matched[0];

  return slugify(slug || nameTr || nameEn || nameDe || category?.id || "kategori");
}

function dedupeCategories(rows) {
  const unique = new Map();

  rows.map(mapCategory).forEach((category) => {
    const key = categoryIdentity(category);
    const id = String(category.id);
    const current = unique.get(key);

    if (!current) {
      unique.set(key, {
        ...category,
        _duplicateIds: [id],
      });
      return;
    }

    if (!current._duplicateIds.includes(id)) {
      current._duplicateIds.push(id);
    }
  });

  return Array.from(unique.values());
}

function mapProduct(row) {
  return {
    ...row,
    name: safeJson(row.name, row.name || { tr: "" }),
    description: safeJson(row.description, row.description || { tr: "" }),
    sizes: safeArray(row.sizes),
    colors: safeArray(row.colors),
    tags: safeArray(row.tags),
    images: safeArray(row.images),
    price: toNumber(row.price),
    compare_price: toNumber(row.compare_price),
    stock_qty: toNumber(row.stock_qty),
    low_stock: toNumber(row.low_stock),
    views: toNumber(row.views),
    sales_count: toNumber(row.sales_count),
    stock_enabled: row.stock_enabled !== false,
    featured: row.featured === true,
    campaign: row.campaign === true,
    new_badge: row.new_badge === true,
    whatsapp_order: row.whatsapp_order === true,


 integrate_stock: row.integrate_stock !== false,
integrate_cash: row.integrate_cash !== false,
integrate_cari: row.integrate_cari !== false,
approval_status: row.approval_status || "approved",
rejection_reason: row.rejection_reason || "",
};


}


function productToForm(product) {
  const p = mapProduct(product);

  return {
    ...emptyProduct,
    ...p,
    name: p.name || { tr: "", en: "", de: "" },
    description: p.description || { tr: "", en: "", de: "" },
    category_id: p.category_id || "",
    price: p.price || "",
    compare_price: p.compare_price || "",
    vat_rate: p.vat_rate ?? "20",
    stock_qty: p.stock_qty ?? "0",
    low_stock: p.low_stock ?? "0",
    sizesText: textList(p.sizes),
    colorsText: textList(p.colors),
    tagsText: textList(p.tags),
    imagesText: textList(p.images),
    cover_image: p.cover_image || "",
  };
}

function payloadFromForm(form) {
  return {
    category_id: form.category_id ? Number(form.category_id) : null,
    name: {
      tr: form.name?.tr || "",
      en: form.name?.en || "",
      de: form.name?.de || "",
    },
    description: {
      tr: form.description?.tr || "",
      en: form.description?.en || "",
      de: form.description?.de || "",
    },
    product_type: form.product_type || "physical",
    sale_type: form.sale_type || "order",
    status: form.status || "active",
    brand: form.brand || "",
    model: form.model || "",
    sku: form.sku || "",
    price: toNumber(form.price),
    compare_price: toNumber(form.compare_price),
    currency: form.currency || "TRY",
    vat_rate: toNumber(form.vat_rate || 20),
    stock_enabled: !!form.stock_enabled,
    stock_qty: toNumber(form.stock_qty),
    low_stock: toNumber(form.low_stock),
    sizes: safeArray(form.sizesText),
    colors: safeArray(form.colorsText),
    tags: safeArray(form.tagsText),
    cover_image: form.cover_image || "",
    images: safeArray(form.imagesText),
    note: form.note || "",
    featured: !!form.featured,
    campaign: !!form.campaign,
    new_badge: !!form.new_badge,
    whatsapp_order: !!form.whatsapp_order,
    integrate_stock: !!form.integrate_stock,
    integrate_cash: !!form.integrate_cash,
    integrate_cari: !!form.integrate_cari,
    views: toNumber(form.views),
    sales_count: toNumber(form.sales_count),
  };
}

function findCategoryId(categories, keywords = []) {
  for (const c of categories) {
    const name = normalizeSearch(localized(c.name, "tr"));
    const slug = normalizeSearch(c.slug || "");

    for (const keyword of keywords) {
      const k = normalizeSearch(keyword);
      if (name.includes(k) || slug.includes(k)) return c.id;
    }
  }

  return "";
}

function presetFor(type, categories, settings) {
  const currency = settings.default_currency || "TRY";

  if (type === "vehicle") {
    return {
      product_type: "vehicle",
      sale_type: "lead",
      category_id: findCategoryId(categories, ["araç", "galeri", "oto", "car"]),
      currency,
      vat_rate: 0,
      stock_enabled: false,
      stock_qty: 1,
      low_stock: 0,
      integrate_stock: false,
      integrate_cash: false,
      integrate_cari: true,
      whatsapp_order: true,
      tagsText: "Araç\nGaleri\nTeklif",
    };
  }

  if (type === "realEstate") {
    return {
      product_type: "realEstate",
      sale_type: "lead",
      category_id: findCategoryId(categories, ["emlak", "ev", "ofis", "real"]),
      currency,
      vat_rate: 0,
      stock_enabled: false,
      stock_qty: 1,
      low_stock: 0,
      integrate_stock: false,
      integrate_cash: false,
      integrate_cari: true,
      whatsapp_order: true,
      tagsText: "Emlak\nKiralık\nSatılık\nTeklif",
    };
  }

  if (type === "service") {
    return {
      product_type: "service",
      sale_type: "whatsapp",
      category_id: findCategoryId(categories, ["hizmet", "paket", "service"]),
      currency,
      vat_rate: 20,
      stock_enabled: false,
      stock_qty: 0,
      low_stock: 0,
      integrate_stock: false,
      integrate_cash: true,
      integrate_cari: true,
      whatsapp_order: true,
      tagsText: "Hizmet\nPaket\nWhatsApp",
    };
  }

  if (type === "digital") {
    return {
      product_type: "digital",
      sale_type: "order",
      category_id: findCategoryId(categories, ["dijital", "online", "digital"]),
      currency,
      vat_rate: 20,
      stock_enabled: false,
      stock_qty: 0,
      low_stock: 0,
      integrate_stock: false,
      integrate_cash: true,
      integrate_cari: true,
      whatsapp_order: false,
      tagsText: "Dijital\nOnline",
    };
  }

  if (type === "electronics") {
    return {
      product_type: "physical",
      sale_type: "order",
      category_id: findCategoryId(categories, ["elektronik", "bilgisayar", "telefon", "electronic"]),
      currency,
      vat_rate: 20,
      stock_enabled: true,
      stock_qty: 10,
      low_stock: 3,
      integrate_stock: true,
      integrate_cash: true,
      integrate_cari: true,
      whatsapp_order: true,
      tagsText: "Elektronik\nKampanya\nKargo",
    };
  }

  if (type === "cosmetic") {
    return {
      product_type: "physical",
      sale_type: "order",
      category_id: findCategoryId(categories, ["kozmetik", "bakım", "cosmetic"]),
      currency,
      vat_rate: 20,
      stock_enabled: true,
      stock_qty: 20,
      low_stock: 5,
      integrate_stock: true,
      integrate_cash: true,
      integrate_cari: true,
      whatsapp_order: true,
      tagsText: "Kozmetik\nBakım\nYeni",
    };
  }

  if (type === "food") {
    return {
      product_type: "physical",
      sale_type: "order",
      category_id: findCategoryId(categories, ["gıda", "market", "food"]),
      currency,
      vat_rate: 10,
      stock_enabled: true,
      stock_qty: 30,
      low_stock: 5,
      integrate_stock: true,
      integrate_cash: true,
      integrate_cari: true,
      whatsapp_order: true,
      tagsText: "Gıda\nMarket\nHızlı Teslimat",
    };
  }

  if (type === "other") {
    return {
      product_type: "physical",
      sale_type: "catalog",
      category_id: findCategoryId(categories, ["diğer", "other", "diger"]),
      currency,
      vat_rate: 20,
      stock_enabled: false,
      stock_qty: 0,
      low_stock: 0,
      integrate_stock: false,
      integrate_cash: true,
      integrate_cari: true,
      whatsapp_order: true,
      tagsText: "Diğer\nÖzel Ürün\nBilgi Al",
    };
  }

  return {
    product_type: "physical",
    sale_type: "order",
    category_id: findCategoryId(categories, ["butik", "giyim", "iç", "fashion"]),
    currency,
    vat_rate: 20,
    stock_enabled: true,
    stock_qty: 10,
    low_stock: 3,
    integrate_stock: true,
    integrate_cash: true,
    integrate_cari: true,
    whatsapp_order: true,
    sizesText: "S\nM\nL\nXL",
    colorsText: "Siyah\nBeyaz\nPudra",
    tagsText: "Butik\nİç Giyim\nKampanya",
  };
}

const primaryButtonSx = {
  borderRadius: 3,
  textTransform: "none",
  fontWeight: 900,
  bgcolor: BRAND.orange,
  boxShadow: "0 14px 32px rgba(245,130,32,.25)",
  "&:hover": { bgcolor: BRAND.orangeDark },
};

const outlineButtonSx = {
  borderRadius: 3,
  textTransform: "none",
  fontWeight: 900,
  borderColor: BRAND.line,
  color: BRAND.ink,
  bgcolor: "white",
  "&:hover": {
    borderColor: alpha(BRAND.orange, 0.45),
    bgcolor: alpha(BRAND.orange, 0.04),
  },
};

function TypeShortcut({ title, subtitle, color, icon, onClick }) {
  return (
    <Card
      className="myp-type-shortcut"
      elevation={0}
      onClick={onClick}
      sx={{
        cursor: "pointer",
        minWidth: 0,
        borderRadius: 5,
        border: `1px solid ${BRAND.line}`,
        bgcolor: "white",
        transition: "all .18s ease",
        animation: `${fadeUp} .35s ease both`,
        "&:hover": {
          transform: "translateY(-4px)",
          borderColor: color,
          boxShadow: "0 18px 40px rgba(15,23,42,.12)",
        },
      }}
    >
      <CardContent sx={{ p: { xs: 1.1, md: 1.4 }, textAlign: "center" }}>
        <Avatar
          sx={{
            mx: "auto",
            width: { xs: 52, md: 62 },
            height: { xs: 52, md: 62 },
            bgcolor: alpha(color, 0.12),
            color,
            border: `2px solid ${alpha(color, 0.35)}`,
            animation: `${floatY} 3.2s ease-in-out infinite`,
          }}
        >
          {icon}
        </Avatar>

        <Typography sx={{ mt: 1, fontWeight: 950, color: BRAND.ink, fontSize: { xs: 12, md: 13 } }}>
          {title}
        </Typography>

        <Typography sx={{ mt: 0.2, fontWeight: 800, color: BRAND.muted, fontSize: { xs: 10.5, md: 11 } }}>
          {subtitle}
        </Typography>
      </CardContent>
    </Card>
  );
}

function StatCard({ icon, title, value, color }) {
  return (
    <Card
      className="myp-stat-card"
      elevation={0}
      sx={{
        borderRadius: 4,
        border: `1px solid ${BRAND.line}`,
        bgcolor: "white",
        boxShadow: "0 16px 40px rgba(15,23,42,.06)",
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
        <Stack direction="row" spacing={1.4} alignItems="center" sx={{ minWidth: 0 }}>
          <Avatar sx={{ bgcolor: alpha(color, 0.12), color, flex: "0 0 auto" }}>
            {icon}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 12.5, color: BRAND.muted, fontWeight: 850 }}>
              {title}
            </Typography>
            <Typography sx={{ fontSize: { xs: 21, md: 24 }, color: BRAND.ink, fontWeight: 950 }}>
              {value}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function EmptyState({ title, desc, action }) {
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 5,
        border: `1px dashed ${alpha(BRAND.blue, 0.28)}`,
        bgcolor: "white",
      }}
    >
      <CardContent sx={{ py: { xs: 4, md: 6 }, textAlign: "center" }}>
        <ImageIcon sx={{ fontSize: 58, color: alpha(BRAND.blue, 0.26), mb: 1 }} />

        <Typography sx={{ fontSize: 22, fontWeight: 950, color: BRAND.ink }}>
          {title}
        </Typography>

        {desc && (
          <Typography sx={{ mt: 1, color: BRAND.muted, fontWeight: 750 }}>
            {desc}
          </Typography>
        )}

        {action && <Box sx={{ mt: 2 }}>{action}</Box>}
      </CardContent>
    </Card>
  );
}

function ProductImageManager({ TT, form, setForm }) {
  const gallery = safeArray(form.imagesText);

  const addGalleryImage = (url) => {
    if (!url) return;

    const next = [...gallery.filter((x) => x !== url), url];

    setForm((prev) => ({
      ...prev,
      cover_image: prev.cover_image || url,
      imagesText: next.join("\n"),
    }));
  };

  const removeGalleryImage = (url) => {
    const next = gallery.filter((x) => x !== url);

    setForm((prev) => ({
      ...prev,
      cover_image: prev.cover_image === url ? next[0] || "" : prev.cover_image,
      imagesText: next.join("\n"),
    }));
  };

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 4,
        border: `1px solid ${BRAND.line}`,
        overflow: "hidden",
        bgcolor: "white",
      }}
    >
      <Box
        sx={{
          p: 2,
          background: `linear-gradient(135deg, ${alpha(BRAND.orange, 0.12)}, ${alpha(
            BRAND.blue,
            0.08
          )})`,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 950, color: BRAND.ink }}>
              {TT.imageInfo}
            </Typography>
            <Typography sx={{ color: BRAND.muted, fontWeight: 750, fontSize: 13 }}>
              Kapak seç, galeri oluştur, mobilde taşmadan göster.
            </Typography>
          </Box>

          <ImageIcon sx={{ color: BRAND.orange }} />
        </Stack>
      </Box>

      <CardContent>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "260px minmax(0,1fr)" },
            gap: 2,
            minWidth: 0,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: "100%",
                aspectRatio: "1 / 1",
                borderRadius: 4,
                border: `1px solid ${BRAND.line}`,
                overflow: "hidden",
                bgcolor: BRAND.bg,
                display: "grid",
                placeItems: "center",
              }}
            >
              {form.cover_image ? (
                <Box
                  component="img"
                  src={form.cover_image}
                  alt="cover"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <Stack alignItems="center" spacing={1} sx={{ color: BRAND.muted }}>
                  <ImageIcon sx={{ fontSize: 56 }} />
                  <Typography sx={{ fontWeight: 900, fontSize: 13 }}>
                    Kapak Görseli
                  </Typography>
                </Stack>
              )}
            </Box>

            <Box sx={{ mt: 1.3 }}>
              <ResimUpload
                onUpload={(url) => {
                  setForm((prev) => ({ ...prev, cover_image: url }));
                  addGalleryImage(url);
                }}
              />
            </Box>

            <TextField
              label="Kapak Görseli URL"
              value={form.cover_image || ""}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, cover_image: e.target.value }))
              }
              fullWidth
              size="small"
              sx={{ mt: 1.2 }}
            />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
              spacing={1}
              sx={{ mb: 1.3 }}
            >
              <Box>
                <Typography sx={{ fontWeight: 950, color: BRAND.ink }}>
                  Galeri Görselleri
                </Typography>
                <Typography sx={{ fontWeight: 750, color: BRAND.muted, fontSize: 12.5 }}>
                  Ürün detayında küçük görseller olarak görünür.
                </Typography>
              </Box>

              <ResimUpload onUpload={addGalleryImage} />
            </Stack>

            {gallery.length === 0 ? (
              <Box
                sx={{
                  minHeight: 150,
                  borderRadius: 4,
                  border: `1px dashed ${alpha(BRAND.blue, 0.28)}`,
                  bgcolor: BRAND.bg,
                  display: "grid",
                  placeItems: "center",
                  textAlign: "center",
                  color: BRAND.muted,
                  p: 2,
                }}
              >
                <Box>
                  <ImageIcon sx={{ fontSize: 44, color: alpha(BRAND.blue, 0.28) }} />
                  <Typography sx={{ fontWeight: 900 }}>
                    Galeriye görsel ekle
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    sm: "repeat(auto-fill, minmax(118px, 1fr))",
                  },
                  gap: 1.2,
                  minWidth: 0,
                }}
              >
                {gallery.map((url) => (
                  <Box
                    key={url}
                    sx={{
                      position: "relative",
                      borderRadius: 3,
                      overflow: "hidden",
                      border:
                        form.cover_image === url
                          ? `2px solid ${BRAND.orange}`
                          : `1px solid ${BRAND.line}`,
                      bgcolor: "white",
                      minWidth: 0,
                    }}
                  >
                    <Box
                      component="img"
                      src={url}
                      alt="gallery"
                      sx={{
                        width: "100%",
                        height: 116,
                        objectFit: "cover",
                        display: "block",
                      }}
                    />

                    <Stack
                      direction="row"
                      spacing={0.4}
                      sx={{
                        position: "absolute",
                        top: 6,
                        right: 6,
                      }}
                    >
                      <Tooltip title="Kapak yap">
                        <IconButton
                          size="small"
                          onClick={() =>
                            setForm((prev) => ({ ...prev, cover_image: url }))
                          }
                          sx={{
                            bgcolor: "white",
                            "&:hover": { bgcolor: "white" },
                          }}
                        >
                          <CheckCircleIcon
                            fontSize="small"
                            color={form.cover_image === url ? "success" : "inherit"}
                          />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Sil">
                        <IconButton
                          size="small"
                          onClick={() => removeGalleryImage(url)}
                          sx={{
                            bgcolor: "white",
                            "&:hover": { bgcolor: "white" },
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                ))}
              </Box>
            )}

            <TextField
              label="Galeri URL Listesi"
              value={form.imagesText || ""}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, imagesText: e.target.value }))
              }
              fullWidth
              multiline
              minRows={3}
              sx={{ mt: 1.3 }}
              helperText="Her satıra bir görsel URL. Yükleme butonu otomatik ekler."
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function MegaCategoryMenu({ open, categories, lang, onSelect, TT }) {
  if (!open) return null;

  const groups = [
    {
      title: "Moda & Giyim",
      icon: "fashion",
      items: ["Butik & İç Giyim", "Ayakkabı", "Çanta", "Aksesuar", "Spor Giyim", "Takı"],
    },
    {
      title: "Araç & Emlak",
      icon: "car",
      items: ["Araç & Galeri", "Emlak", "Kiralık", "Satılık", "Arsa", "Ofis"],
    },
    {
      title: "Teknoloji",
      icon: "computer",
      items: ["Elektronik", "Bilgisayar", "Telefon", "Tablet", "Aksesuar", "Oyun"],
    },
    {
      title: "Yaşam & Diğer",
      icon: "other",
      items: ["Kozmetik", "Gıda & Market", "Hizmet & Paket", "Dijital Ürün", "Eğitim", TT.other],
    },
  ];

  return (
    <Box
      sx={{
        position: { xs: "static", md: "absolute" },
        left: { md: 0 },
        right: { md: 0 },
        top: { md: "100%" },
        zIndex: 30,
        bgcolor: "rgba(15,23,42,.36)",
        px: { xs: 0, md: 3 },
        pb: { xs: 0, md: 3 },
      }}
    >
      <Card
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: "none",
          mx: "auto",
          borderRadius: { xs: 0, md: 4 },
          border: `1px solid ${BRAND.line}`,
          overflow: "hidden",
          animation: `${fadeUp} .18s ease both`,
        }}
      >
        <CardContent sx={{ p: { xs: 1.5, md: 2.2 } }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "220px minmax(0,1fr)" },
              gap: 2,
              minWidth: 0,
            }}
          >
            <Stack spacing={1}>
              {categories.map((c) => (
                <Button
                  key={c.id}
                  onClick={() => onSelect(c)}
                  startIcon={categoryIcon(c.icon)}
                  endIcon={<ExpandMoreIcon />}
                  sx={{
                    justifyContent: "space-between",
                    textTransform: "none",
                    fontWeight: 900,
                    borderRadius: 3,
                    color: c.color || BRAND.ink,
                    bgcolor: alpha(c.color || BRAND.orange, 0.08),
                    minWidth: 0,
                  }}
                >
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                    {localized(c.name, lang)}
                  </span>
                </Button>
              ))}
            </Stack>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0,1fr))", lg: "repeat(4, minmax(0,1fr))" },
                gap: 2,
                minWidth: 0,
              }}
            >
              {groups.map((g) => (
                <Box key={g.title} sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: alpha(BRAND.orange, 0.14),
                        color: BRAND.orange,
                      }}
                    >
                      {categoryIcon(g.icon)}
                    </Avatar>
                    <Typography sx={{ fontWeight: 950, color: BRAND.orange }}>
                      {g.title}
                    </Typography>
                  </Stack>

                  <Stack spacing={0.6}>
                    {g.items.map((item) => (
                      <Typography
                        key={item}
                        onClick={() => {
                          const found = categories.find((c) =>
                            normalizeSearch(localized(c.name, "tr")).includes(
                              normalizeSearch(item.split("&")[0].trim())
                            )
                          );
                          if (found) onSelect(found);
                        }}
                        sx={{
                          fontWeight: 800,
                          color: BRAND.ink,
                          cursor: "pointer",
                          fontSize: 14,
                          "&:hover": { color: BRAND.orange },
                        }}
                      >
                        {item}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              ))}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

function ProductCard({
  TT,
  product,
  category,
  lang,
  onPreview,
  onEdit,
  onDelete,
  onDuplicate,
}) {
  const title = localized(product.name, lang) || localized(product.name, "tr") || "Ürün";
  const desc = localized(product.description, lang);
  const image = product.cover_image || safeArray(product.images)[0] || "";
  const discount = percentDiscount(product);
  const stockQty = toNumber(product.stock_qty);
  const outOfStock = product.stock_enabled && stockQty <= 0;

  return (
    <Card
      className="myp-product-card"
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: 4,
        border: `1px solid ${BRAND.line}`,
        overflow: "hidden",
        bgcolor: "white",
        transition: "all .18s ease",
        minWidth: 0,
        animation: `${fadeUp} .35s ease both`,
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 18px 42px rgba(15,23,42,.12)",
        },
      }}
    >
      <Box sx={{ position: "relative", height: { xs: 218, md: 245 }, bgcolor: BRAND.bg }}>
        {image ? (
          <Box
            component="img"
            src={image}
            alt={title}
            onClick={onPreview}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              cursor: "pointer",
              transition: "transform .3s ease",
              "&:hover": { transform: "scale(1.04)" },
            }}
          />
        ) : (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ height: "100%", color: BRAND.muted }}
          >
            {productTypeIcon(product.product_type)}
            <Typography sx={{ mt: 1, fontWeight: 900 }}>Görsel Yok</Typography>
          </Stack>
        )}

        <IconButton
          size="small"
          sx={{
            position: "absolute",
            right: 10,
            top: 10,
            bgcolor: "white",
            boxShadow: "0 8px 20px rgba(15,23,42,.12)",
            "&:hover": { bgcolor: "white" },
          }}
        >
          <FavoriteBorderIcon />
        </IconButton>

        <Stack spacing={0.7} sx={{ position: "absolute", left: 10, top: 10 }}>
          {product.campaign && (


            <Chip
              size="small"
              label={TT.campaign}
              sx={{
                bgcolor: BRAND.orange,
                color: "white",
                fontWeight: 950,
              }}
            />
          )}

          {discount > 0 && (
            <Chip
              size="small"
              label={`%${discount}`}
              sx={{
                bgcolor: BRAND.danger,
                color: "white",
                fontWeight: 950,
              }}
            />
          )}

          {product.featured && (
            <Chip
              size="small"
              label={TT.featured}
              color="primary"
              sx={{ fontWeight: 950 }}
            />
          )}
        </Stack>

        {outOfStock && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "rgba(15,23,42,.58)",
              display: "grid",
              placeItems: "center",
            }}
          >
            <Chip
              label="Tükendi"
              color="error"
              sx={{ fontWeight: 950, fontSize: 16 }}
            />
          </Box>
        )}
      </Box>

      <CardContent sx={{ p: 1.6 }}>
        <Stack spacing={1} sx={{ minWidth: 0 }}>


        <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap>
  <Tooltip title={product.rejection_reason || ""}>
    <Chip
      size="small"
      label={approvalStatusLabel(
        product.approval_status,
        lang
      )}
      color={statusColor(product.approval_status)}
      sx={{ fontWeight: 950 }}
    />
  </Tooltip>

  {category && (


              <Chip
                size="small"
                icon={categoryIcon(category.icon)}
                label={localized(category.name, lang)}
                sx={{ fontWeight: 850, maxWidth: "100%" }}
              />
            )}

            <Chip
              size="small"
              icon={productTypeIcon(product.product_type)}
              label={productTypeLabel(TT, product.product_type)}
              sx={{
                bgcolor: alpha(BRAND.blue, 0.07),
                color: BRAND.blue,
                fontWeight: 900,
              }}
            />
          </Stack>

          <Typography
            onClick={onPreview}
            sx={{
              fontWeight: 900,
              color: BRAND.ink,
              fontSize: 15.5,
              lineHeight: 1.25,
              minHeight: 39,
              cursor: "pointer",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              "&:hover": { color: BRAND.orange },
            }}
          >
            {title}
          </Typography>

          <Typography
            sx={{
              color: BRAND.muted,
              fontWeight: 700,
              fontSize: 12.7,
              minHeight: 36,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {desc || `${product.brand || ""} ${product.model || ""}`.trim() || "Açıklama yok"}
          </Typography>

          <Stack direction="row" spacing={0.8} alignItems="center">
            <Typography sx={{ fontWeight: 950, color: BRAND.ink, fontSize: 13 }}>
              4.8
            </Typography>
            <StarIcon sx={{ fontSize: 17, color: "#FBBF24" }} />
            <Typography sx={{ fontWeight: 750, color: BRAND.muted, fontSize: 12 }}>
              ({toNumber(product.views) + 24})
            </Typography>
            <Typography sx={{ fontWeight: 800, color: BRAND.muted, fontSize: 12 }}>
              · {toNumber(product.sales_count)}
            </Typography>
          </Stack>

          <Stack spacing={0.2}>
            {!!toNumber(product.compare_price) && (
              <Typography
                sx={{
                  color: BRAND.muted,
                  textDecoration: "line-through",
                  fontWeight: 800,
                  fontSize: 13,
                }}
              >
                {money(product.compare_price, product.currency, lang)}
              </Typography>
            )}

            <Typography
              sx={{
                color: BRAND.orange,
                fontWeight: 950,
                fontSize: 22,
                letterSpacing: -0.3,
              }}
            >
              {money(product.price, product.currency, lang)}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              icon={<LocalShippingIcon />}
              label="Kargo"
              sx={{
                bgcolor: alpha(BRAND.green, 0.10),
                color: "#16803F",
                fontWeight: 850,
              }}
            />

            <Chip
              size="small"
              label={saleTypeLabel(TT, product.sale_type)}
              sx={{
                bgcolor: alpha(BRAND.orange, 0.10),
                color: BRAND.orangeDark,
                fontWeight: 850,
              }}
            />
          </Stack>

          <Divider />

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={0.6}>
              <Tooltip title="Önizle">
                <IconButton
                  size="small"
                  onClick={onPreview}
                  sx={{ border: `1px solid ${BRAND.line}` }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Kopyala">
                <IconButton
                  size="small"
                  onClick={onDuplicate}
                  sx={{ border: `1px solid ${BRAND.line}` }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>

            <Stack direction="row" spacing={0.6}>
              <Tooltip title="Düzenle">
                <IconButton
                  size="small"
                  onClick={onEdit}
                  sx={{
                    border: `1px solid ${BRAND.line}`,
                    color: BRAND.orange,
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Pasifleştir">
                <IconButton
                  size="small"
                  onClick={onDelete}
                  sx={{
                    border: `1px solid ${BRAND.line}`,
                    color: BRAND.danger,
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

function ProductPreviewDialog({ TT, open, onClose, product, category, lang, onEdit }) {
  const [selectedImage, setSelectedImage] = useState("");

  useEffect(() => {
    if (!product) return;

    const images = [product.cover_image, ...safeArray(product.images)].filter(Boolean);
    setSelectedImage(images[0] || "");
  }, [product]);

  if (!product) return null;

  const images = [product.cover_image, ...safeArray(product.images)].filter(Boolean);
  const title = localized(product.name, lang) || localized(product.name, "tr") || "Ürün";
  const desc = localized(product.description, lang);
  const discount = percentDiscount(product);

  return (
    <Dialog
      className="myp-marketplace-dialog"
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography sx={{ fontWeight: 950, color: BRAND.ink }}>
            {TT.detailPreview}
          </Typography>

          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: "white" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(0, 0.95fr) minmax(0, 1.05fr)",
              xl: "minmax(0, 560px) minmax(0, 1fr) minmax(0, 290px)",
            },
            gap: 3,
            minWidth: 0,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box
              sx={{
                height: { xs: 320, md: 520 },
                borderRadius: 4,
                border: `1px solid ${BRAND.line}`,
                overflow: "hidden",
                bgcolor: BRAND.bg,
                position: "relative",
                display: "grid",
                placeItems: "center",
              }}
            >
              {selectedImage ? (
                <Box
                  component="img"
                  src={selectedImage}
                  alt={title}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    bgcolor: "#fff",
                  }}
                />
              ) : (
                <Stack alignItems="center" spacing={1} sx={{ color: BRAND.muted }}>
                  <ImageIcon sx={{ fontSize: 70 }} />
                  <Typography sx={{ fontWeight: 950 }}>Görsel Yok</Typography>
                </Stack>
              )}

              {discount > 0 && (
                <Chip
                  label={`%${discount}`}
                  sx={{
                    position: "absolute",
                    left: 14,
                    top: 14,
                    bgcolor: BRAND.danger,
                    color: "white",
                    fontWeight: 950,
                  }}
                />
              )}

              <Box
                sx={{
                  position: "absolute",
                  left: 14,
                  bottom: 14,
                  right: 14,
                  bgcolor: alpha(BRAND.green, 0.9),
                  color: "white",
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  fontWeight: 950,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>Sepette / Teklif Akışında Uygulanacak</span>
                <LocalShippingIcon fontSize="small" />
              </Box>
            </Box>

            {images.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ mt: 1.5, overflowX: "auto", pb: 0.5 }}>
                {images.map((url) => (
                  <Box
                    key={url}
                    onClick={() => setSelectedImage(url)}
                    sx={{
                      width: 74,
                      height: 74,
                      borderRadius: 2,
                      overflow: "hidden",
                      flex: "0 0 auto",
                      cursor: "pointer",
                      border:
                        selectedImage === url
                          ? `2px solid ${BRAND.orange}`
                          : `1px solid ${BRAND.line}`,
                    }}
                  >
                    <Box
                      component="img"
                      src={url}
                      alt="thumb"
                      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  </Box>
                ))}
              </Stack>
            )}
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Stack spacing={1.6}>
              <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                {category && (
                  <Chip
                    size="small"
                    icon={categoryIcon(category.icon)}
                    label={localized(category.name, lang)}
                    sx={{ fontWeight: 900 }}
                  />
                )}

                <Chip
                  size="small"
                  icon={productTypeIcon(product.product_type)}
                  label={productTypeLabel(TT, product.product_type)}
                  sx={{
                    bgcolor: alpha(BRAND.blue, 0.08),
                    color: BRAND.blue,
                    fontWeight: 900,
                  }}
                />

                <Chip
                  size="small"
                  label={saleTypeLabel(TT, product.sale_type)}
                  sx={{
                    bgcolor: alpha(BRAND.orange, 0.12),
                    color: BRAND.orangeDark,
                    fontWeight: 900,
                  }}
                />
              </Stack>

              <Typography sx={{ fontWeight: 950, color: BRAND.ink, fontSize: { xs: 22, md: 27 }, lineHeight: 1.2 }}>
                {title}
              </Typography>

              <Stack direction="row" spacing={0.9} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography sx={{ fontWeight: 950 }}>4.8</Typography>
                <StarIcon sx={{ color: "#FBBF24" }} />
                <Typography sx={{ color: BRAND.muted, fontWeight: 800 }}>
                  {toNumber(product.views) + 124}
                </Typography>
                <Typography sx={{ color: BRAND.muted, fontWeight: 800 }}>
                  · {toNumber(product.sales_count)}
                </Typography>
              </Stack>

              <Typography sx={{ color: BRAND.danger, fontWeight: 900 }}>
                Popüler ürün! Son 24 saatte {Math.max(15, toNumber(product.views))} kişi görüntüledi.
              </Typography>

              <Divider />

              <Box>
                {!!toNumber(product.compare_price) && (
                  <Typography
                    sx={{
                      color: BRAND.muted,
                      textDecoration: "line-through",
                      fontWeight: 850,
                      fontSize: 17,
                    }}
                  >
                    {money(product.compare_price, product.currency, lang)}
                  </Typography>
                )}

                <Typography sx={{ color: BRAND.orange, fontWeight: 950, fontSize: { xs: 28, md: 34 } }}>
                  {money(product.price, product.currency, lang)}
                </Typography>
              </Box>

              {safeArray(product.colors).length > 0 && (
                <Box>
                  <Typography sx={{ fontWeight: 950, mb: 1 }}>
                    {TT.colors}
                  </Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {safeArray(product.colors).map((c) => (
                      <Chip key={c} label={c} sx={{ fontWeight: 900 }} />
                    ))}
                  </Stack>
                </Box>
              )}

              {safeArray(product.sizes).length > 0 && (
                <Box>
                  <Typography sx={{ fontWeight: 950, mb: 1 }}>
                    {TT.sizes}
                  </Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {safeArray(product.sizes).map((s) => (
                      <Chip key={s} label={s} variant="outlined" sx={{ fontWeight: 900 }} />
                    ))}
                  </Stack>
                </Box>
              )}

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ pt: 1 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  sx={{
                    ...outlineButtonSx,
                    borderColor: BRAND.orange,
                    color: BRAND.orange,
                    py: 1.4,
                    fontSize: 16,
                  }}
                >
                  {TT.nowBuy}
                </Button>

                <Button
                  variant="contained"
                  fullWidth
                  startIcon={
                    product.sale_type === "lead" ? <RequestQuoteIcon /> : <ShoppingCartIcon />
                  }
                  sx={{ ...primaryButtonSx, py: 1.4, fontSize: 16 }}
                >
                  {product.sale_type === "lead"
                    ? TT.requestOffer
                    : product.sale_type === "whatsapp"
                    ? TT.whatsappBuy
                    : TT.addBasket}
                </Button>

                <IconButton sx={{ border: `1px solid ${BRAND.line}`, width: 54, height: 54 }}>
                  <FavoriteBorderIcon />
                </IconButton>
              </Stack>

              <Alert severity="success" icon={<LocalShippingIcon />} sx={{ borderRadius: 3, fontWeight: 850 }}>
                {TT.deliveryInfo}
              </Alert>

              <Box>
                <Typography sx={{ fontWeight: 950, mb: 1 }}>
                  {TT.description}
                </Typography>

                <Typography sx={{ color: BRAND.ink, fontWeight: 750, whiteSpace: "pre-wrap" }}>
                  {desc || "Ürün açıklaması eklenmemiş."}
                </Typography>
              </Box>

              {safeArray(product.tags).length > 0 && (
                <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                  {safeArray(product.tags).map((tag) => (
                    <Chip
                      key={tag}
                      size="small"
                      label={tag}
                      sx={{
                        bgcolor: alpha(BRAND.orange, 0.08),
                        color: BRAND.orangeDark,
                        fontWeight: 900,
                      }}
                    />
                  ))}
                </Stack>
              )}
            </Stack>
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Stack spacing={1.5}>
              <Card elevation={0} sx={{ borderRadius: 4, border: `1px solid ${BRAND.line}` }}>
                <CardContent>
                  <Typography sx={{ fontWeight: 950, color: BRAND.ink, mb: 1 }}>
                    {TT.storeInfo}
                  </Typography>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar sx={{ bgcolor: BRAND.orange }}>
                      <StorefrontIcon />
                    </Avatar>

                    <Box>
                      <Typography sx={{ fontWeight: 950 }}>
                        MesYat Mağaza
                      </Typography>
                      <Typography sx={{ fontWeight: 800, color: BRAND.green, fontSize: 13 }}>
                        Güvenilir Satıcı
                      </Typography>
                    </Box>
                  </Stack>

                  <Button fullWidth variant="contained" sx={{ ...outlineButtonSx, mt: 2 }}>
                    Mağazaya Git
                  </Button>
                </CardContent>
              </Card>

              <Card elevation={0} sx={{ borderRadius: 4, border: `1px solid ${BRAND.line}` }}>
                <CardContent>
                  <Typography sx={{ fontWeight: 950, color: BRAND.ink, mb: 1 }}>
                    {TT.campaigns}
                  </Typography>

                  <Stack spacing={1}>
                    <Alert severity="success" sx={{ borderRadius: 2, fontWeight: 850 }}>
                      Kargo / teslimat bilgisi aktif
                    </Alert>

                    {product.campaign && (
                      <Alert severity="warning" sx={{ borderRadius: 2, fontWeight: 850 }}>
                        Sepette indirim / kampanya
                      </Alert>
                    )}

                    {product.whatsapp_order && (
                      <Alert severity="info" sx={{ borderRadius: 2, fontWeight: 850 }}>
                        WhatsApp iletişim aktif
                      </Alert>
                    )}
                  </Stack>
                </CardContent>
              </Card>

              <Button
                variant="contained"
                startIcon={<EditIcon />}
                sx={primaryButtonSx}
                onClick={() => onEdit(product)}
              >
                {TT.productEdit}
              </Button>
            </Stack>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}


function formatDateTime(value, lang = "tr") {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const locale = lang === "de" ? "de-DE" : lang === "en" ? "en-GB" : "tr-TR";
  return date.toLocaleString(locale, {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function SettingSwitchCard({ icon, title, description, checked, onChange, disabled = false }) {
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3.2,
        border: `1px solid ${checked ? alpha(BRAND.blue, 0.25) : BRAND.line}`,
        bgcolor: checked ? alpha(BRAND.blueSoft, 0.65) : "white",
        opacity: disabled ? 0.65 : 1,
        height: "100%",
      }}
    >
      <CardContent sx={{ p: "16px !important" }}>
        <Stack direction="row" spacing={1.4} alignItems="flex-start">
          <Avatar
            sx={{
              width: 42,
              height: 42,
              bgcolor: checked ? BRAND.blue : alpha(BRAND.muted, 0.12),
              color: checked ? "white" : BRAND.muted,
            }}
          >
            {icon}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 950, color: BRAND.ink }}>{title}</Typography>
            <Typography sx={{ mt: 0.35, color: BRAND.muted, fontSize: 12.5, fontWeight: 700, lineHeight: 1.45 }}>
              {description}
            </Typography>
          </Box>
          <Switch checked={checked} onChange={onChange} disabled={disabled} />
        </Stack>
      </CardContent>
    </Card>
  );
}

function SettingsSection({ icon, title, description, children }) {
  return (
    <Card elevation={0} sx={{ borderRadius: 4, border: `1px solid ${BRAND.line}`, overflow: "visible" }}>
      <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
        <Stack direction="row" spacing={1.4} alignItems="center" sx={{ mb: 2.2 }}>
          <Avatar sx={{ bgcolor: BRAND.orangeSoft, color: BRAND.orange, width: 44, height: 44 }}>
            {icon}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 950, fontSize: 18, color: BRAND.ink }}>{title}</Typography>
            <Typography sx={{ color: BRAND.muted, fontWeight: 700, fontSize: 12.5 }}>{description}</Typography>
          </Box>
        </Stack>
        {children}
      </CardContent>
    </Card>
  );
}

function LeadCard({ lead, lang, settings, onStatus, onOpen }) {
  const seller = lead.seller || {};
  const status = lead.status || "pending";
  const customerName = lead.customer?.name || lead.customer_name || "Alıcı";
  const customerPhone = lead.customer?.phone || lead.customer_phone || "";
  const customerEmail = lead.customer?.email || lead.customer_email || "";
  const isWaiting = ["pending", "new"].includes(status);
  const isSent = ["shared", "approved", "viewed", "contacted", "won", "lost"].includes(status);

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 4,
        border: `1px solid ${isWaiting ? alpha(BRAND.warning, 0.38) : BRAND.line}`,
        overflow: "hidden",
        boxShadow: isWaiting ? `0 14px 34px ${alpha(BRAND.warning, 0.08)}` : "none",
      }}
    >
      <Box
        sx={{
          px: { xs: 1.6, md: 2.2 },
          py: 1.15,
          bgcolor: isWaiting ? alpha(BRAND.warning, 0.09) : alpha(BRAND.blue, 0.045),
          borderBottom: `1px solid ${BRAND.line}`,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <NotificationsActiveIcon sx={{ color: isWaiting ? BRAND.warning : BRAND.blue, fontSize: 20 }} />
            <Typography sx={{ fontWeight: 950, color: BRAND.ink, fontSize: 13.5 }}>
              {isWaiting ? "ONAY BEKLEYEN TALEP" : leadStatusLabel(status, lang).toUpperCase()}
            </Typography>
          </Stack>
          <Chip size="small" label={leadStatusLabel(status, lang)} color={statusColor(status)} sx={{ fontWeight: 900 }} />
        </Stack>
      </Box>

      <CardContent sx={{ p: { xs: 1.6, md: 2.2 } }}>
        <Stack direction={{ xs: "column", lg: "row" }} spacing={2} alignItems={{ xs: "stretch", lg: "flex-start" }}>
          <Avatar
            variant="rounded"
            src={lead.cover_image || ""}
            sx={{ width: { xs: "100%", sm: 112 }, height: 112, bgcolor: BRAND.blueSoft, color: BRAND.blue, borderRadius: 3 }}
          >
            <RequestQuoteIcon />
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 950, color: BRAND.ink, fontSize: 19 }}>
              {lead.product_name || `Talep #${lead.id}`}
            </Typography>
            <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap sx={{ mt: 0.8 }}>
              <Chip
                size="small"
                icon={<StoreMallDirectoryIcon />}
                label={seller.store_name || seller.institution_name || settings.store_name || "Satıcı işletme"}
                sx={{ fontWeight: 850, bgcolor: BRAND.blueSoft, color: BRAND.blue }}
              />
              {lead.category_name && (
                <Chip size="small" label={localized(lead.category_name, lang) || "Kategori"} sx={{ fontWeight: 850 }} />
              )}
              {lead.created_at && (
                <Chip size="small" icon={<ScheduleIcon />} label={formatDateTime(lead.created_at, lang)} sx={{ fontWeight: 800 }} />
              )}
            </Stack>

            <Box sx={{ mt: 1.4, p: 1.35, borderRadius: 2.5, bgcolor: BRAND.bg, border: `1px solid ${BRAND.line}` }}>
              <Typography sx={{ color: BRAND.muted, fontSize: 11, fontWeight: 900, mb: 0.45 }}>ALICI MESAJI</Typography>
              <Typography sx={{ color: BRAND.ink, fontWeight: 750, lineHeight: 1.55 }}>
                {lead.message || "Alıcı açıklama eklemedi."}
              </Typography>
            </Box>

            <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap sx={{ mt: 1.35 }}>
              <Chip icon={<PersonSearchIcon />} label={customerName} sx={{ fontWeight: 850 }} />
              {customerPhone && <Chip icon={<PhoneIcon />} label={customerPhone} sx={{ fontWeight: 850 }} />}
              {customerEmail && <Chip icon={<EmailIcon />} label={customerEmail} sx={{ fontWeight: 850 }} />}
              {(seller.city || seller.district) && (
                <Chip icon={<PlaceIcon />} label={[seller.city, seller.district].filter(Boolean).join(" / ")} sx={{ fontWeight: 850 }} />
              )}
            </Stack>

            {toNumber(lead.budget) > 0 && (
              <Typography sx={{ mt: 1.2, color: BRAND.orange, fontWeight: 950, fontSize: 18 }}>
                Alıcı bütçesi: {money(lead.budget, lead.currency || settings.default_currency, lang)}
              </Typography>
            )}
          </Box>

          <Stack spacing={1} sx={{ width: { xs: "100%", lg: 275 }, flex: "0 0 auto" }}>
            {isWaiting && (
              <Button
                variant="contained"
                startIcon={<MarkEmailReadIcon />}
                sx={{ ...primaryButtonSx, minHeight: 46 }}
                onClick={() => onStatus(lead, "shared")}
              >
                Onayla ve Satıcıya Gönder
              </Button>
            )}

            {isWaiting && (
              <Stack direction="row" spacing={1}>
                <Button fullWidth variant="outlined" color="error" startIcon={<CloseIcon />} onClick={() => onStatus(lead, "rejected")}>
                  Reddet
                </Button>
                <Button fullWidth variant="outlined" startIcon={<BlockIcon />} onClick={() => onStatus(lead, "spam")}>
                  Spam
                </Button>
              </Stack>
            )}

            {isSent && (
              <Alert severity="success" icon={<MarkEmailReadIcon />} sx={{ borderRadius: 2.5, fontWeight: 850 }}>
                Talep satıcı işletmeye iletildi. Finansal kayıt oluşturulmadı.
              </Alert>
            )}

            {lead._scope === "local" && ["shared", "approved", "viewed"].includes(status) && (
              <Button
                variant="contained"
                startIcon={<PhoneIcon />}
                sx={{ ...primaryButtonSx, bgcolor: BRAND.blue, "&:hover": { bgcolor: BRAND.blue2 } }}
                onClick={() => onStatus(lead, "contacted")}
              >
                İletişime Geçildi
              </Button>
            )}

            {lead._scope === "local" && status === "contacted" && (
              <Stack direction="row" spacing={1}>
                <Button fullWidth variant="contained" color="success" onClick={() => onStatus(lead, "won")}>
                  Satışa Döndü
                </Button>
                <Button fullWidth variant="outlined" color="error" onClick={() => onStatus(lead, "lost")}>
                  Satış Olmadı
                </Button>
              </Stack>
            )}

            <Button variant="outlined" startIcon={<VisibilityIcon />} sx={outlineButtonSx} onClick={() => onOpen(lead)}>
              Talep Detayı
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

function LeadDetailDialog({ open, lead, lang, onClose, onStatus }) {
  if (!lead) return null;
  const seller = lead.seller || {};
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth className="myp-marketplace-dialog">
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Box>
            <Typography sx={{ fontWeight: 950, color: BRAND.ink, fontSize: 21 }}>Pazar Yeri Talebi #{lead.id}</Typography>
            <Typography sx={{ color: BRAND.muted, fontWeight: 750, fontSize: 12.5 }}>
              {formatDateTime(lead.created_at, lang)}
            </Typography>
          </Box>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info" icon={<ShieldOutlinedIcon />} sx={{ borderRadius: 3, fontWeight: 750 }}>
            MesYat yalnızca alıcı ile satıcıyı buluşturur. Ödeme, teslimat ve satış sözleşmesi taraflar arasında gerçekleştirilir.
          </Alert>
          <SettingsSection icon={<StoreMallDirectoryIcon />} title="Satıcı İşletme" description="Talep onaylandığında bu işletmeye bildirim oluşturulur.">
            <Typography sx={{ fontWeight: 950, color: BRAND.ink, fontSize: 18 }}>
              {seller.store_name || seller.institution_name || `Kurum ${lead.kurum_id}`}
            </Typography>
            <Typography sx={{ color: BRAND.muted, fontWeight: 750, mt: 0.4 }}>
              {[seller.city, seller.district].filter(Boolean).join(" / ") || "Konum bilgisi bulunmuyor"}
            </Typography>
          </SettingsSection>
          <SettingsSection icon={<PersonSearchIcon />} title="Alıcı Bilgileri" description="Yalnızca talebin değerlendirilmesi için kullanılır.">
            <Stack spacing={0.8}>
              <Typography><b>Ad Soyad:</b> {lead.customer?.name || lead.customer_name || "-"}</Typography>
              <Typography><b>Telefon:</b> {lead.customer?.phone || lead.customer_phone || "-"}</Typography>
              <Typography><b>E-posta:</b> {lead.customer?.email || lead.customer_email || "-"}</Typography>
              <Typography><b>Mesaj:</b> {lead.message || "-"}</Typography>
            </Stack>
          </SettingsSection>
        </Stack>
      </DialogContent>
      <DialogActions className="myp-marketplace-dialog-actions">
        {["pending", "new"].includes(lead.status) && (
          <>
            <Button color="error" variant="outlined" onClick={() => onStatus(lead, "rejected")}>Reddet</Button>
            <Button variant="contained" startIcon={<MarkEmailReadIcon />} sx={primaryButtonSx} onClick={() => onStatus(lead, "shared")}>
              Onayla ve Satıcıya Gönder
            </Button>
          </>
        )}
        <Button onClick={onClose}>Kapat</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function MarketplaceYonetimiPage() {
  const [lang, setLang] = useState(getLang());
  const TT = TEXT[lang] || TEXT.tr;

  const canDeleteCategory =
    getCurrentUserEmail() === "mesyat@admin.com";

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);

  const [settings, setSettings] = useState(defaultSettings);
  const [categories, setCategories] = useState(fallbackCategories);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [leads, setLeads] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [leadDetail, setLeadDetail] = useState(null);

  const [filters, setFilters] = useState({
    q: "",
    category: "all",
    productType: "all",
    saleType: "all",
    status: "all",
    sort: "popular",
  });

  const [productOpen, setProductOpen] = useState(false);
  const [productForm, setProductForm] = useState(emptyProduct);

  const [categoryOpen, setCategoryOpen] = useState(false);
  const [categoryForm, setCategoryForm] = useState(emptyCategory);

  const [previewProduct, setPreviewProduct] = useState(null);

  const [snack, setSnack] = useState({
    open: false,
    text: "",
    severity: "success",
  });

  useEffect(() => {
    const syncLang = () => setLang(getLang());

    window.addEventListener("app_lang_change", syncLang);
    window.addEventListener("storage", syncLang);

    return () => {
      window.removeEventListener("app_lang_change", syncLang);
      window.removeEventListener("storage", syncLang);
    };
  }, []);

  useEffect(() => {
    loadAll();
  }, []);

  function show(text, severity = "success") {
    setSnack({ open: true, text, severity });
  }

  async function loadLeadData() {
    try {
      const globalRes = await marketplaceApi("/marketplace/global/leads");
      return (globalRes.data || []).map((lead) => ({ ...lead, _scope: "global" }));
    } catch (_) {
      const localRes = await marketplaceApi("/marketplace/leads");
      return (localRes.data || []).map((lead) => ({ ...lead, _scope: "local" }));
    }
  }

  async function loadNotifications() {
    try {
      const res = await marketplaceApi("/marketplace/notifications");
      return res.data || [];
    } catch (_) {
      return [];
    }
  }

  async function loadAll() {
    setLoading(true);

    try {
      // Ayarlar çağrısı ilk mağaza açılışındaki güvenli bootstrap işlemini tamamlar.
      const settingsRes = await marketplaceApi("/marketplace/settings");
      setSettings(settingsRes.data || defaultSettings);

      const [categoriesRes, productsRes, leadRows, notificationRows] = await Promise.all([
        marketplaceApi("/marketplace/categories"),
        marketplaceApi("/marketplace/products"),
        loadLeadData(),
        loadNotifications(),
      ]);

    const apiCategories = Array.isArray(categoriesRes.data)
  ? dedupeCategories(
      categoriesRes.data.filter(
        (category) => category.active !== false
      )
    )
  : dedupeCategories(fallbackCategories);

      setCategories(apiCategories);
      setProducts((productsRes.data || []).map(mapProduct));
      setOrders([]);
      setLeads(leadRows);
      setNotifications(notificationRows);
    } catch (err) {
      show(`Veriler alınamadı: ${err.message}`, "error");
    } finally {
      setLoading(false);
    }
  }

  const categoryMap = useMemo(() => {
    const map = new Map();
    categories.forEach((category) => {
      map.set(String(category.id), category);

      safeArray(category._duplicateIds).forEach((duplicateId) => {
        map.set(String(duplicateId), category);
      });
    });
    return map;
  }, [categories]);

  const stats = useMemo(() => {
    const activeProducts = products.filter((p) => p.status === "active").length;
    const newLeads = leads.filter((l) => ["pending", "new"].includes(l.status)).length;
    const sharedLeads = leads.filter((l) =>
      ["shared", "approved", "viewed", "contacted", "won", "lost"].includes(l.status)
    ).length;

    return { activeProducts, newLeads, sharedLeads };
  }, [products, leads]);

  const filteredProducts = useMemo(() => {
    const q = normalizeSearch(filters.q);

    let list = products.filter((p) => {
      const category = categoryMap.get(String(p.category_id));

      const haystack = normalizeSearch(
        [
          localized(p.name, "tr"),
          localized(p.name, "en"),
          localized(p.name, "de"),
          localized(p.description, "tr"),
          p.brand,
          p.model,
          p.sku,
          localized(category?.name, "tr"),
          safeArray(p.tags).join(" "),
        ].join(" ")
      );

      if (q && !haystack.includes(q)) return false;

      if (filters.category !== "all") {
        const selectedCategory = categoryMap.get(String(filters.category));
        const productCategory = categoryMap.get(String(p.category_id));

        if (
          String(productCategory?.id || p.category_id) !==
          String(selectedCategory?.id || filters.category)
        ) {
          return false;
        }
      }

      if (filters.productType !== "all" && p.product_type !== filters.productType) {
        return false;
      }

      if (filters.saleType !== "all" && p.sale_type !== filters.saleType) {
        return false;
      }

      if (filters.status !== "all" && p.status !== filters.status) {
        return false;
      }

      return true;
    });

    if (filters.sort === "priceAsc") {
      list = [...list].sort((a, b) => toNumber(a.price) - toNumber(b.price));
    }

    if (filters.sort === "priceDesc") {
      list = [...list].sort((a, b) => toNumber(b.price) - toNumber(a.price));
    }

    if (filters.sort === "popular") {
      list = [...list].sort((a, b) => toNumber(b.views) - toNumber(a.views));
    }

    if (filters.sort === "newest") {
      list = [...list].sort((a, b) => toNumber(b.id) - toNumber(a.id));
    }

    return list;
  }, [products, filters, categoryMap]);

  const popularProducts = useMemo(() => {
    return [...products]
      .sort((a, b) => {
        const aScore = toNumber(a.views) + toNumber(a.sales_count) * 10;
        const bScore = toNumber(b.views) + toNumber(b.sales_count) * 10;
        return bScore - aScore;
      })
      .slice(0, 10);
  }, [products]);

  function openNewProduct(type = "physical") {
    const preset = presetFor(type, categories, settings);

    setProductForm({
      ...emptyProduct,
      ...preset,
      name: { tr: "", en: "", de: "" },
      description: { tr: "", en: "", de: "" },
    });

    setProductOpen(true);
  }

  function openEditProduct(product) {
    const form = productToForm(product);
    const canonicalCategory = categoryMap.get(String(form.category_id));

    setProductForm({
      ...form,
      category_id: canonicalCategory?.id || form.category_id,
    });
    setProductOpen(true);
  }

  function openDuplicateProduct(product) {
    const form = productToForm(product);
    const canonicalCategory = categoryMap.get(String(form.category_id));

    setProductForm({
      ...form,
      id: null,
      category_id: canonicalCategory?.id || form.category_id,
      sku: `${form.sku || "COPY"}-COPY`,
      name: {
        ...form.name,
        tr: `${form.name?.tr || "Ürün"} Kopya`,
      },
    });

    setProductOpen(true);
  }

  async function saveProduct() {
    const payload = payloadFromForm(productForm);

    if (!payload.name.tr.trim()) {
      show(TT.requiredName, "error");
      return;
    }

    if (!payload.category_id) {
      show(TT.requiredCategory, "error");
      return;
    }

    if (payload.price <= 0) {
      show(TT.requiredPrice, "error");
      return;
    }

    setLoading(true);

 try {
  let result;

  if (productForm.id) {
    result = await marketplaceApi(
      `/marketplace/products/${productForm.id}`,
      {
        method: "PUT",
        body: JSON.stringify(payload),
      }
    );
  } else {
    result = await marketplaceApi(
      "/marketplace/products",
      {
        method: "POST",
        body: JSON.stringify(payload),
      }
    );
  }

  setProductOpen(false);

  show(
    result?.message ||
      (productForm.id
        ? TT.productUpdated
        : TT.productSaved)
  );

  await loadAll();



    } catch (err) {
      show(err.message, "error");
    } finally {
      setLoading(false);
    }
  }

  async function deleteProduct(product) {
    const ok = window.confirm("Bu ürün pasifleştirilsin mi?");
    if (!ok) return;

    setLoading(true);

    try {
      await marketplaceApi(`/marketplace/products/${product.id}`, {
        method: "DELETE",
      });

      show(TT.productDeleted);
      await loadAll();
    } catch (err) {
      show(err.message, "error");
    } finally {
      setLoading(false);
    }
  }

  function openNewCategory() {
    setCategoryForm(emptyCategory);
    setCategoryOpen(true);
  }

  async function saveCategory() {
    const tr = categoryForm.name.tr.trim();

    if (!tr) {
      show(TT.requiredCategory, "error");
      return;
    }

    setLoading(true);

    try {
      await marketplaceApi("/marketplace/categories", {
        method: "POST",
        body: JSON.stringify({
          name: categoryForm.name,
          slug: categoryForm.slug || slugify(tr),
          icon: categoryForm.icon || "category",
          color: categoryForm.color || BRAND.orange,
          active: categoryForm.active !== false,
        }),
      });

      setCategoryOpen(false);
      show(TT.categorySaved);
      await loadAll();
    } catch (err) {
      show(err.message, "error");
    } finally {
      setLoading(false);
    }
  }


async function deleteCategory(category) {
  if (!canDeleteCategory) return;

  const categoryName =
    localized(category.name, "tr") || "Bu kategori";

  const ok = window.confirm(
    `"${categoryName}" kategorisi silinsin mi?`
  );

  if (!ok) return;

  setLoading(true);

  try {
    await marketplaceApi(
      `/marketplace/categories/${category.id}`,
      {
        method: "DELETE",
      }
    );

    show("Kategori silindi.");
    await loadAll();
  } catch (err) {
    show(err.message, "error");
  } finally {
    setLoading(false);
  }
}


  async function updateOrderStatus(order, status) {
    setLoading(true);

    try {
      await marketplaceApi(`/marketplace/orders/${order.id}/status`, {
        method: "PUT",
        body: JSON.stringify({ status }),
      });

      show("Sipariş durumu güncellendi.");
      await loadAll();
    } catch (err) {
      show(err.message, "error");
    } finally {
      setLoading(false);
    }
  }

  async function updateLeadStatus(lead, status) {
    setLoading(true);

    try {
      const path =
        lead?._scope === "global"
          ? `/marketplace/global/leads/${lead.id}/status`
          : `/marketplace/leads/${lead.id}/status`;

      const res = await marketplaceApi(path, {
        method: "PUT",
        body: JSON.stringify({ status }),
      });

      setLeadDetail(null);
      show(
        status === "shared"
          ? "Talep onaylandı ve satıcı işletmeye bildirim oluşturuldu."
          : res.message || "Talep durumu güncellendi."
      );
      await loadAll();
    } catch (err) {
      show(err.message, "error");
    } finally {
      setLoading(false);
    }
  }

  function openLeadDetail(lead) {
    setLeadDetail(lead);

    if (lead?._scope === "local" && ["shared", "approved"].includes(lead.status)) {
      marketplaceApi(`/marketplace/leads/${lead.id}/status`, {
        method: "PUT",
        body: JSON.stringify({ status: "viewed" }),
      })
        .then(() => loadAll())
        .catch(() => {});
    }
  }

  async function saveSettings() {
    setLoading(true);

    try {
      const res = await marketplaceApi("/marketplace/settings", {
        method: "POST",
        body: JSON.stringify({
          ...settings,
          order_active: false,
          auto_stock_decrease: false,
          auto_cash_record: false,
          auto_cari_record: false,
        }),
      });

      setSettings(res.data || settings);
      show(TT.settingsSaved);
    } catch (err) {
      show(err.message, "error");
    } finally {
      setLoading(false);
    }
  }

  function selectCategory(category) {
    setFilters((prev) => ({
      ...prev,
      category: String(category.id),
    }));
    setTab(1);
    setMegaOpen(false);
  }

  const shortcuts = [
    {
      key: "physical",
      title: "Butik / İç Giyim",
      subtitle: TT.orderSale,
      icon: <CheckroomIcon />,
      color: BRAND.pink,
    },
    {
      key: "vehicle",
      title: TT.vehicle,
      subtitle: TT.leadSale,
      icon: <DirectionsCarIcon />,
      color: BRAND.blue,
    },
    {
      key: "realEstate",
      title: TT.realEstate,
      subtitle: TT.leadSale,
      icon: <HomeWorkIcon />,
      color: BRAND.green,
    },
    {
      key: "electronics",
      title: "Elektronik",
      subtitle: TT.orderSale,
      icon: <ComputerIcon />,
      color: BRAND.blue2,
    },
    {
      key: "cosmetic",
      title: "Kozmetik",
      subtitle: TT.orderSale,
      icon: <SpaIcon />,
      color: BRAND.violet,
    },
    {
      key: "food",
      title: "Gıda & Market",
      subtitle: TT.orderSale,
      icon: <RestaurantIcon />,
      color: BRAND.warning,
    },
    {
      key: "service",
      title: TT.service,
      subtitle: TT.whatsappSale,
      icon: <VerifiedIcon />,
      color: BRAND.orange,
    },
    {
      key: "digital",
      title: TT.digital,
      subtitle: TT.orderSale,
      icon: <BoltIcon />,
      color: BRAND.dark,
    },
    {
      key: "other",
      title: TT.other,
      subtitle: TT.catalogSale,
      icon: <MoreHorizIcon />,
      color: BRAND.muted,
    },
  ];

  return (
    <Box
      className="myp-marketplace-page"
      sx={{
        minHeight: "100vh",
        bgcolor: "#fff",
        color: BRAND.ink,
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflowX: "hidden",
      }}
    >
      <style>{MARKETPLACE_RESPONSIVE_CSS}</style>

      <Box
        className="myp-marketplace-header"
        sx={{
          borderBottom: `1px solid ${BRAND.line}`,
          bgcolor: "white",
          position: "sticky",
          top: 0,
          zIndex: 20,
        }}
      >
        <Box
          className="myp-marketplace-header-inner"
          sx={{
            width: "100%",
            maxWidth: "none",
            mx: "auto",
            px: { xs: 1.5, md: 3 },
            py: 1.5,
            minWidth: 0,
          }}
        >
          <Stack
            className="myp-marketplace-header-row"
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
            sx={{ minWidth: 0 }}
          >
            <Stack direction="row" alignItems="center" spacing={1.2}>
              <StorefrontIcon sx={{ color: BRAND.orange, fontSize: 34 }} />
              <Typography
                sx={{
                  fontSize: { xs: 24, md: 32 },
                  fontWeight: 950,
                  color: BRAND.ink,
                  letterSpacing: -1.1,
                  whiteSpace: "nowrap",
                }}
              >
                MesYatPazar
              </Typography>
            </Stack>

            <TextField
              className="myp-marketplace-header-search"
              value={filters.q}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, q: e.target.value }))
              }
              placeholder={TT.search}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: BRAND.orange }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: "100%", md: "auto" },
                flex: { md: "1 1 320px" },
                minWidth: 0,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  bgcolor: "#F3F4F6",
                  fontWeight: 800,
                },
              }}
            />

            <Stack
              className="myp-marketplace-header-actions"
              direction="row"
              spacing={1}
              justifyContent="flex-end"
              sx={{ minWidth: 0, flexShrink: 0 }}
            >
              <Button
                startIcon={<RefreshIcon />}
                onClick={loadAll}
                variant="outlined"
                sx={outlineButtonSx}
              >
                {TT.refresh}
              </Button>

              <Button
                startIcon={<AddIcon />}
                onClick={() => openNewProduct("physical")}
                variant="contained"
                sx={primaryButtonSx}
              >
                {TT.addProduct}
              </Button>
            </Stack>
          </Stack>
        </Box>

        <Box
          sx={{
            borderTop: `1px solid ${alpha(BRAND.line, 0.7)}`,
            position: "relative",
          }}
        >
          <Stack
            className="myp-marketplace-category-strip"
            direction="row"
            spacing={1.2}
            sx={{
              width: "100%",
              maxWidth: "none",
              mx: "auto",
              px: { xs: 1.5, md: 3 },
              py: 1.2,
              overflowX: "auto",
              minWidth: 0,
              "&::-webkit-scrollbar": { height: 6 },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: alpha(BRAND.orange, 0.35),
                borderRadius: 99,
              },
            }}
          >
            <Chip
              icon={<CategoryIcon />}
              label={TT.categories}
              onClick={() => setMegaOpen((p) => !p)}
              sx={{ fontWeight: 950, flex: "0 0 auto" }}
            />

          {categories.map((c) => (
  <Chip
    key={c.id}
    icon={categoryIcon(c.icon)}
    label={localized(c.name, lang)}
    onClick={() => selectCategory(c)}
    onDelete={
      canDeleteCategory
        ? () => deleteCategory(c)
        : undefined
    }
    deleteIcon={
      canDeleteCategory
        ? <DeleteIcon sx={{ fontSize: 18 }} />
        : undefined
    }
    sx={{
      fontWeight: 900,
      bgcolor: alpha(c.color || BRAND.orange, 0.08),
      color: c.color || BRAND.orange,
      flex: "0 0 auto",
      "& .MuiChip-deleteIcon": {
        color: "#d32f2f",
      },
    }}
  />
))}

            <Chip
              icon={<CampaignIcon />}
              label={TT.campaign}
              sx={{ bgcolor: BRAND.orange, color: "white", fontWeight: 950, flex: "0 0 auto" }}
            />
          </Stack>

          <MegaCategoryMenu
            open={megaOpen}
            categories={categories}
            lang={lang}
            onSelect={selectCategory}
            TT={TT}
          />
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ height: 5 }} />}

      <Box
        className="myp-marketplace-content"
        sx={{
          width: "100%",
          maxWidth: "none",
          mx: "auto",
          px: { xs: 1.5, md: 3 },
          py: 2,
          minWidth: 0,
        }}
      >
        <Card
          className="myp-hero-card"
          elevation={0}
          sx={{
            borderRadius: 5,
            border: `1px solid ${BRAND.line}`,
            background: `linear-gradient(135deg, ${BRAND.dark}, ${BRAND.blue} 55%, ${BRAND.orange})`,
            color: "white",
            overflow: "hidden",
            mb: 2,
            position: "relative",
            "&:before": {
              content: '""',
              position: "absolute",
              inset: 0,
              background:
                "radial-gradient(circle at 20% 20%, rgba(255,255,255,.16), transparent 34%)",
            },
            "&:after": {
              content: '""',
              position: "absolute",
              top: 0,
              bottom: 0,
              width: "38%",
              background:
                "linear-gradient(90deg, transparent, rgba(255,255,255,.16), transparent)",
              animation: `${shine} 4.5s linear infinite`,
            },
          }}
        >
          <CardContent sx={{ p: { xs: 2.2, md: 3.2 }, position: "relative", zIndex: 1 }}>
            <Stack
              className="myp-hero-layout"
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Chip
                  label={TT.pageTitle}
                  sx={{
                    bgcolor: "rgba(255,255,255,.18)",
                    color: "white",
                    fontWeight: 950,
                    mb: 1.2,
                  }}
                />

                <Typography sx={{ fontSize: { xs: 27, md: 40 }, fontWeight: 950, letterSpacing: -1.2 }}>
                  {TT.heroTitle}
                </Typography>

                <Typography
                  sx={{
                    mt: 0.8,
                    color: "rgba(255,255,255,.85)",
                    fontWeight: 800,
                    maxWidth: 920,
                  }}
                >
                  {TT.heroDesc}
                </Typography>
              </Box>

              <Stack
                className="myp-hero-integrations"
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
              >
                <Chip icon={<WarehouseIcon />} label={TT.stockModule} sx={{ bgcolor: "white", fontWeight: 950 }} />
                <Chip icon={<PointOfSaleIcon />} label={TT.cashModule} sx={{ bgcolor: "white", fontWeight: 950 }} />
                <Chip icon={<GroupsIcon />} label={TT.cariModule} sx={{ bgcolor: "white", fontWeight: 950 }} />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Box
          className="myp-stats-grid"
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, minmax(0, 1fr))",
              md: "repeat(4, minmax(0, 1fr))",
            },
            gap: 1.5,
            mb: 2,
            minWidth: 0,
          }}
        >
          <StatCard icon={<Inventory2Icon />} title={TT.totalProducts} value={products.length} color={BRAND.blue} />
          <StatCard icon={<CheckCircleIcon />} title={TT.activeProducts} value={stats.activeProducts} color={BRAND.success} />
          <StatCard icon={<MarkEmailReadIcon />} title={TT.pendingOrders} value={stats.sharedLeads} color={BRAND.success} />
          <StatCard icon={<RequestQuoteIcon />} title={TT.newLeads} value={stats.newLeads} color={BRAND.orange} />
        </Box>

        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            border: `1px solid ${BRAND.line}`,
            mb: 2,
            overflow: "hidden",
          }}
        >
          <Tabs
            value={tab}
            onChange={(e, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              px: 1,
              "& .MuiTab-root": {
                minHeight: 58,
                textTransform: "none",
                fontWeight: 950,
              },
            }}
          >
            <Tab icon={<StorefrontIcon />} iconPosition="start" label={TT.showcase} />
            <Tab icon={<Inventory2Icon />} iconPosition="start" label={TT.products} />
            <Tab icon={<RequestQuoteIcon />} iconPosition="start" label={TT.leads} />
            <Tab icon={<SettingsIcon />} iconPosition="start" label={TT.settings} />
          </Tabs>
        </Card>

        {tab === 0 && (
          <Stack spacing={2.2}>
            <Card elevation={0} sx={{ borderRadius: 5, border: `1px solid ${BRAND.line}` }}>
              <CardContent>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", md: "center" }}
                  spacing={1.5}
                  sx={{ mb: 2 }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 23, fontWeight: 950, color: BRAND.ink }}>
                      {TT.whatSell}
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontWeight: 800 }}>
                      {TT.whatSellDesc}
                    </Typography>
                  </Box>

                  <Button
                    startIcon={<CategoryIcon />}
                    onClick={openNewCategory}
                    variant="outlined"
                    sx={outlineButtonSx}
                  >
                    {TT.addCategory}
                  </Button>
                </Stack>

                <Box
                  className="myp-shortcut-grid"
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat(2, minmax(0, 1fr))",
                      sm: "repeat(3, minmax(0, 1fr))",
                      md: "repeat(5, minmax(0, 1fr))",
                      xl: "repeat(9, minmax(0, 1fr))",
                    },
                    gap: 1.2,
                    minWidth: 0,
                  }}
                >
                  {shortcuts.map((s) => (
                    <TypeShortcut
                      key={s.key}
                      title={s.title}
                      subtitle={s.subtitle}
                      icon={s.icon}
                      color={s.color}
                      onClick={() => openNewProduct(s.key)}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>

            <Card
              elevation={0}
              sx={{
                borderRadius: 5,
                border: `1px solid ${BRAND.line}`,
                bgcolor: "#FAFAFA",
              }}
            >
              <CardContent>
                <Stack
                  className="myp-popular-title-row"
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 2 }}
                >
                  <Typography sx={{ fontSize: 22, fontWeight: 950, color: BRAND.ink }}>
                    {TT.popularProducts}
                  </Typography>

                  <Button
                    onClick={() => setTab(1)}
                    sx={{ textTransform: "none", fontWeight: 950, color: BRAND.ink }}
                  >
                    {TT.seeAll}
                  </Button>
                </Stack>

                {popularProducts.length === 0 ? (
                  <EmptyState
                    title={TT.noProduct}
                    desc={TT.noProductDesc}
                    action={
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        sx={primaryButtonSx}
                        onClick={() => openNewProduct("physical")}
                      >
                        {TT.addProduct}
                      </Button>
                    }
                  />
                ) : (
                  <Box
                    className="myp-products-grid"
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                        lg: "repeat(4, minmax(0, 1fr))",
                        xl: "repeat(5, minmax(0, 1fr))",
                      },
                      gap: 1.5,
                      minWidth: 0,
                    }}
                  >
                    {popularProducts.map((p) => (
                      <ProductCard
                        key={p.id}
                        TT={TT}
                        product={p}
                        category={categoryMap.get(String(p.category_id))}
                        lang={lang}
                        onPreview={() => setPreviewProduct(p)}
                        onEdit={() => openEditProduct(p)}


                        onDelete={() => deleteProduct(p)}
                        onDuplicate={() => openDuplicateProduct(p)}


                      />
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Stack>
        )}

        {tab === 1 && (
          <Box
            className="myp-products-layout"
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "280px minmax(0, 1fr)" },
              gap: 2,
              minWidth: 0,
            }}
          >
            <Card
              elevation={0}
              sx={{
                borderRadius: 5,
                border: `1px solid ${BRAND.line}`,
                alignSelf: "start",
                position: { lg: "sticky" },
                top: { lg: 132 },
                minWidth: 0,
              }}
            >
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <TuneIcon sx={{ color: BRAND.orange }} />
                  <Typography sx={{ fontWeight: 950, color: BRAND.ink }}>
                    {TT.filters}
                  </Typography>
                </Stack>

                <Stack spacing={1.5}>
                  <TextField
                    size="small"
                    value={filters.q}
                    onChange={(e) =>
                      setFilters((prev) => ({ ...prev, q: e.target.value }))
                    }
                    placeholder={TT.search}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <FormControl size="small" fullWidth>
                    <InputLabel>{TT.category}</InputLabel>
                    <Select
                      label={TT.category}
                      value={filters.category}
                      onChange={(e) =>
                        setFilters((prev) => ({ ...prev, category: e.target.value }))
                      }
                    >
                      <MenuItem value="all">{TT.all}</MenuItem>
                      {categories.map((c) => (
                        <MenuItem key={c.id} value={String(c.id)}>
                          {localized(c.name, lang)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size="small" fullWidth>
                    <InputLabel>{TT.productType}</InputLabel>
                    <Select
                      label={TT.productType}
                      value={filters.productType}
                      onChange={(e) =>
                        setFilters((prev) => ({ ...prev, productType: e.target.value }))
                      }
                    >
                      <MenuItem value="all">{TT.all}</MenuItem>
                      {FLOW.productTypes.map((x) => (
                        <MenuItem key={x} value={x}>
                          {productTypeLabel(TT, x)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size="small" fullWidth>
                    <InputLabel>{TT.saleType}</InputLabel>
                    <Select
                      label={TT.saleType}
                      value={filters.saleType}
                      onChange={(e) =>
                        setFilters((prev) => ({ ...prev, saleType: e.target.value }))
                      }
                    >
                      <MenuItem value="all">{TT.all}</MenuItem>
                      {FLOW.saleTypes.map((x) => (
                        <MenuItem key={x} value={x}>
                          {saleTypeLabel(TT, x)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size="small" fullWidth>
                    <InputLabel>{TT.status}</InputLabel>
                    <Select
                      label={TT.status}
                      value={filters.status}
                      onChange={(e) =>
                        setFilters((prev) => ({ ...prev, status: e.target.value }))
                      }
                    >
                      <MenuItem value="all">{TT.all}</MenuItem>
                      {FLOW.statuses.map((x) => (
                        <MenuItem key={x} value={x}>
                          {statusLabel(TT, x)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size="small" fullWidth>
                    <InputLabel>{TT.sort}</InputLabel>
                    <Select
                      label={TT.sort}
                      value={filters.sort}
                      onChange={(e) =>
                        setFilters((prev) => ({ ...prev, sort: e.target.value }))
                      }
                    >
                      <MenuItem value="popular">{TT.popular}</MenuItem>
                      <MenuItem value="newest">{TT.newest}</MenuItem>
                      <MenuItem value="priceAsc">{TT.priceAsc}</MenuItem>
                      <MenuItem value="priceDesc">{TT.priceDesc}</MenuItem>
                    </Select>
                  </FormControl>

                  <Button
                    variant="outlined"
                    sx={outlineButtonSx}
                    onClick={() =>
                      setFilters({
                        q: "",
                        category: "all",
                        productType: "all",
                        saleType: "all",
                        status: "all",
                        sort: "popular",
                      })
                    }
                  >
                    {TT.clearFilters}
                  </Button>

                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    sx={primaryButtonSx}
                    onClick={() => openNewProduct("physical")}
                  >
                    {TT.addProduct}
                  </Button>

                  <Button
                    variant="outlined"
                    startIcon={<CategoryIcon />}
                    sx={outlineButtonSx}
                    onClick={openNewCategory}
                  >
                    {TT.addCategory}
                  </Button>
                </Stack>
              </CardContent>
            </Card>

            <Box sx={{ minWidth: 0 }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
                spacing={1.5}
                sx={{ mb: 1.5 }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 950, color: BRAND.ink, fontSize: 24 }}>
                    {TT.products}
                  </Typography>
                  <Typography sx={{ color: BRAND.muted, fontWeight: 800 }}>
                    {filteredProducts.length} ürün listeleniyor.
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    icon={<FlashOnIcon />}
                    label="Flaş"
                    sx={{
                      bgcolor: alpha(BRAND.danger, 0.10),
                      color: BRAND.danger,
                      fontWeight: 950,
                    }}
                  />
                  <Chip
                    icon={<StarIcon />}
                    label="Puanlı"
                    sx={{
                      bgcolor: alpha(BRAND.warning, 0.13),
                      color: BRAND.warning,
                      fontWeight: 950,
                    }}
                  />
                  <Chip
                    icon={<LocalShippingIcon />}
                    label="Teslimat"
                    sx={{
                      bgcolor: alpha(BRAND.green, 0.12),
                      color: BRAND.green,
                      fontWeight: 950,
                    }}
                  />
                </Stack>
              </Stack>

              {filteredProducts.length === 0 ? (
                <EmptyState
                  title={TT.noFiltered}
                  desc={TT.noFilteredDesc}
                  action={
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      sx={primaryButtonSx}
                      onClick={() => openNewProduct("physical")}
                    >
                      {TT.addProduct}
                    </Button>
                  }
                />
              ) : (
                <Box
                  className="myp-products-grid"
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                      lg: "repeat(3, minmax(0, 1fr))",
                      xl: "repeat(4, minmax(0, 1fr))",
                    },
                    gap: 1.7,
                    minWidth: 0,
                  }}
                >
                  {filteredProducts.map((p) => (
                    <ProductCard
                      key={p.id}
                      TT={TT}
                      product={p}
                      category={categoryMap.get(String(p.category_id))}
                      lang={lang}
                      onPreview={() => setPreviewProduct(p)}
                      onEdit={() => openEditProduct(p)}
                      onDelete={() => deleteProduct(p)}
                      onDuplicate={() => openDuplicateProduct(p)}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        )}

        {tab === -1 && (
          <Stack spacing={1.5}>
            {orders.length === 0 ? (
              <EmptyState title={TT.noOrders} desc={TT.noOrdersDesc} />
            ) : (
              orders.map((order) => (
                <Card key={order.id} elevation={0} sx={{ borderRadius: 4, border: `1px solid ${BRAND.line}` }}>
                  <CardContent>
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", md: "center" }}
                      spacing={2}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography sx={{ fontWeight: 950, color: BRAND.ink }}>
                            {order.order_no || `Sipariş #${order.id}`}
                          </Typography>
                          <Chip
                            label={orderStatusLabel(order.status, lang)}
                            color={statusColor(order.status)}
                            sx={{ fontWeight: 900 }}
                            size="small"
                          />
                        </Stack>

                        <Typography sx={{ mt: 0.7, color: BRAND.muted, fontWeight: 800 }}>
                          {order.customer?.name || order.customer_name || "Müşteri"} ·{" "}
                          {order.customer?.phone || order.customer_phone || ""}
                        </Typography>

                        <Typography sx={{ mt: 1, color: BRAND.orange, fontWeight: 950, fontSize: 22 }}>
                          {money(order.total_amount, order.currency || settings.default_currency, lang)}
                        </Typography>
                      </Box>

                      <FormControl
                        size="small"
                        sx={{ width: { xs: "100%", md: "auto" }, minWidth: { xs: 0, md: 220 } }}
                      >
                        <InputLabel>{TT.status}</InputLabel>
                        <Select
                          label={TT.status}
                          value={order.status || "new"}
                          onChange={(e) => updateOrderStatus(order, e.target.value)}
                        >
                          {FLOW.orderStatuses.map((s) => (
                            <MenuItem key={s} value={s}>
                              {orderStatusLabel(s, lang)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Stack>
                  </CardContent>
                </Card>
              ))
            )}
          </Stack>
        )}

        {tab === 2 && (
          <Stack spacing={1.5}>
            <Alert
              severity="info"
              icon={<ShieldOutlinedIcon />}
              sx={{ borderRadius: 3.5, border: `1px solid ${alpha(BRAND.blue, 0.16)}`, fontWeight: 750 }}
            >
              MesYat, Letgo benzeri ilan modelinde yalnızca alıcı ile satıcıyı buluşturur. Talep onayı finansal kayıt veya ödeme oluşturmaz.
            </Alert>

            {notifications.filter((n) => !n.is_read).length > 0 && (
              <Alert severity="warning" icon={<NotificationsActiveIcon />} sx={{ borderRadius: 3.5, fontWeight: 850 }}>
                {notifications.filter((n) => !n.is_read).length} okunmamış pazar yeri bildirimi bulunuyor.
              </Alert>
            )}

            {leads.length === 0 ? (
              <EmptyState title={TT.noLeads} desc={TT.noLeadsDesc} />
            ) : (
              leads.map((lead) => (
                <LeadCard
                  key={lead.id}
                  lead={lead}
                  lang={lang}
                  settings={settings}
                  onStatus={updateLeadStatus}
                  onOpen={openLeadDetail}
                />
              ))
            )}
          </Stack>
        )}

        {tab === 3 && (
          <Stack spacing={2}>
            <Box>
              <Typography sx={{ fontWeight: 950, fontSize: { xs: 25, md: 30 }, color: BRAND.ink }}>
                Mağaza Ayarları
              </Typography>
              <Typography sx={{ mt: 0.5, color: BRAND.muted, fontWeight: 750 }}>
                İşletme vitrini, iletişim ve talep akışını sade ve güvenli biçimde yönetin.
              </Typography>
            </Box>

            <Alert severity="info" icon={<ShieldOutlinedIcon />} sx={{ borderRadius: 3.5, fontWeight: 780 }}>
              MesYat ödeme almaz, komisyon kesmez ve satış sözleşmesinin tarafı değildir. Alıcı ve satıcı ödeme ile teslimatı kendi aralarında belirler.
            </Alert>

            <SettingsSection
              icon={<StorefrontIcon />}
              title="Mağaza Kimliği"
              description="Alıcıların ürün kartında ve satıcı bölümünde göreceği bilgiler."
            >
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0,1fr))" }, gap: 2 }}>
                <TextField
                  label={TT.storeName}
                  value={settings.store_name || ""}
                  onChange={(e) => setSettings((prev) => ({ ...prev, store_name: e.target.value }))}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label={TT.storeSlug}
                  value={settings.store_slug || ""}
                  onChange={(e) => setSettings((prev) => ({ ...prev, store_slug: slugify(e.target.value) }))}
                  fullWidth
                  helperText="Örnek: mesyat-dijital-magaza"
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label={TT.logoUrl}
                  value={settings.logo_url || ""}
                  onChange={(e) => setSettings((prev) => ({ ...prev, logo_url: e.target.value }))}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.4, p: 1.2, border: `1px dashed ${BRAND.line}`, borderRadius: 3 }}>
                  <Avatar src={settings.logo_url || ""} sx={{ width: 54, height: 54, bgcolor: BRAND.blueSoft, color: BRAND.blue }}>
                    <StorefrontIcon />
                  </Avatar>
                  <Box>
                    <Typography sx={{ fontWeight: 900, color: BRAND.ink }}>Logo Önizlemesi</Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: 12, fontWeight: 700 }}>Logo yoksa mağaza baş harfi gösterilir.</Typography>
                  </Box>
                </Box>
                <TextField
                  label={TT.storeDesc}
                  value={localized(settings.store_description, "tr")}
                  onChange={(e) => setSettings((prev) => ({
                    ...prev,
                    store_description: { ...safeJson(prev.store_description, {}), tr: e.target.value },
                  }))}
                  fullWidth
                  multiline
                  minRows={4}
                  InputLabelProps={{ shrink: true }}
                  sx={{ gridColumn: { md: "1 / -1" } }}
                />
              </Box>
            </SettingsSection>

            <SettingsSection
              icon={<PhoneIcon />}
              title="İletişim ve Para Birimi"
              description="MesYat ödeme almaz; bu bilgiler alıcının satıcıyla iletişime geçmesini kolaylaştırır."
            >
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0,1fr))" }, gap: 2 }}>
                <TextField
                  label={TT.whatsappNumber}
                  value={settings.whatsapp_number || ""}
                  onChange={(e) => setSettings((prev) => ({ ...prev, whatsapp_number: e.target.value.replace(/[^0-9+]/g, "") }))}
                  fullWidth
                  placeholder="905xxxxxxxxx"
                  InputLabelProps={{ shrink: true }}
                />
                <FormControl fullWidth>
                  <InputLabel>{TT.defaultCurrency}</InputLabel>
                  <Select
                    label={TT.defaultCurrency}
                    value={settings.default_currency || "TRY"}
                    onChange={(e) => setSettings((prev) => ({ ...prev, default_currency: e.target.value }))}
                  >
                    {FLOW.currencies.map((currency) => <MenuItem key={currency} value={currency}>{currency}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
            </SettingsSection>

            <SettingsSection
              icon={<LocalShippingIcon />}
              title="Teslimat, İade ve Güvenlik Açıklaması"
              description="Tarafların işlem öncesinde göreceği açık kurallar."
            >
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0,1fr))" }, gap: 2 }}>
                <TextField
                  label={TT.delivery}
                  value={localized(settings.delivery_info, "tr")}
                  onChange={(e) => setSettings((prev) => ({
                    ...prev,
                    delivery_info: { ...safeJson(prev.delivery_info, {}), tr: e.target.value },
                  }))}
                  fullWidth
                  multiline
                  minRows={4}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label={TT.returnPolicy}
                  value={localized(settings.return_policy, "tr")}
                  onChange={(e) => setSettings((prev) => ({
                    ...prev,
                    return_policy: { ...safeJson(prev.return_policy, {}), tr: e.target.value },
                  }))}
                  fullWidth
                  multiline
                  minRows={4}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            </SettingsSection>

            <SettingsSection
              icon={<SettingsIcon />}
              title="Sistem Özellikleri"
              description="Yalnızca ilan ve talep akışı aktiftir; ödeme ve otomatik finans kayıtları kapalıdır."
            >
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0,1fr))", xl: "repeat(3, minmax(0,1fr))" }, gap: 1.4 }}>
                <SettingSwitchCard
                  icon={<StorefrontIcon />}
                  title="Mağaza Aktif"
                  description="Ürün ve ilan vitrini mobil uygulamada yayınlanır."
                  checked={settings.store_active !== false}
                  onChange={(e) => setSettings((prev) => ({ ...prev, store_active: e.target.checked }))}
                />
                <SettingSwitchCard
                  icon={<RequestQuoteIcon />}
                  title="Talep Formu Aktif"
                  description="Alıcılar satıcı işletmeye bilgi ve görüşme talebi gönderebilir."
                  checked={settings.lead_active !== false}
                  onChange={(e) => setSettings((prev) => ({ ...prev, lead_active: e.target.checked }))}
                />
                <SettingSwitchCard
                  icon={<ShieldOutlinedIcon />}
                  title="Yönetici Onayı"
                  description="Global pazardan gelen talepler satıcıya iletilmeden önce yönetici tarafından incelenir."
                  checked={settings.require_approval !== false}
                  onChange={(e) => setSettings((prev) => ({ ...prev, require_approval: e.target.checked }))}
                />
                <SettingSwitchCard
                  icon={<PaidIcon />}
                  title="MesYat Üzerinden Ödeme"
                  description="Letgo modelinde ödeme taraflar arasında yapılır. Bu özellik güvenlik için kapalıdır."
                  checked={false}
                  onChange={() => {}}
                  disabled
                />
                <SettingSwitchCard
                  icon={<PointOfSaleIcon />}
                  title="Otomatik Kasa Kaydı"
                  description="Talep onayı satış değildir; kasa hareketi oluşturulmaz."
                  checked={false}
                  onChange={() => {}}
                  disabled
                />
                <SettingSwitchCard
                  icon={<GroupsIcon />}
                  title="Otomatik Cari Kaydı"
                  description="Alıcı talebi finansal borç veya alacak oluşturmaz."
                  checked={false}
                  onChange={() => {}}
                  disabled
                />
              </Box>
            </SettingsSection>

            <Card
              elevation={0}
              sx={{
                position: "sticky",
                bottom: 10,
                zIndex: 5,
                borderRadius: 3.5,
                border: `1px solid ${alpha(BRAND.orange, 0.25)}`,
                boxShadow: `0 16px 40px ${alpha(BRAND.dark, 0.14)}`,
                bgcolor: alpha("#FFFFFF", 0.97),
                backdropFilter: "blur(12px)",
              }}
            >
              <CardContent sx={{ p: "14px 16px !important" }}>
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={1.2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <InfoOutlinedIcon sx={{ color: BRAND.blue }} />
                    <Typography sx={{ color: BRAND.muted, fontWeight: 750, fontSize: 12.5 }}>
                      Değişiklikler yalnızca “Ayarları Kaydet” düğmesine basıldığında uygulanır.
                    </Typography>
                  </Stack>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    sx={{ ...primaryButtonSx, minHeight: 46, px: 3 }}
                    onClick={saveSettings}
                    disabled={loading}
                  >
                    {loading ? "Kaydediliyor..." : TT.saveSettings}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        )}
      </Box>

      <LeadDetailDialog
        open={Boolean(leadDetail)}
        lead={leadDetail}
        lang={lang}
        onClose={() => setLeadDetail(null)}
        onStatus={updateLeadStatus}
      />

      <Dialog
        className="myp-marketplace-dialog"
        open={productOpen}
        onClose={() => setProductOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 950, color: BRAND.ink, fontSize: 22 }}>
                {productForm.id ? TT.productEdit : TT.productAdd}
              </Typography>
              <Typography sx={{ color: BRAND.muted, fontWeight: 750, fontSize: 13 }}>
                {TT.productAddDesc}
              </Typography>
            </Box>

            <IconButton onClick={() => setProductOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ bgcolor: BRAND.bg }}>
          <Stack spacing={2}>
            <Alert severity="info" sx={{ borderRadius: 3, fontWeight: 850 }}>
              {TT.infoHint}
            </Alert>

            <Card elevation={0} sx={{ borderRadius: 4, border: `1px solid ${BRAND.line}` }}>
              <CardContent>
                <Typography sx={{ fontWeight: 950, color: BRAND.ink, mb: 1.5 }}>
                  {TT.selectSellType}
                </Typography>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat(2, minmax(0, 1fr))",
                      sm: "repeat(3, minmax(0, 1fr))",
                      lg: "repeat(5, minmax(0, 1fr))",
                    },
                    gap: 1.2,
                    minWidth: 0,
                  }}
                >
                  {shortcuts.map((s) => (
                    <TypeShortcut
                      key={s.key}
                      title={s.title}
                      subtitle={s.subtitle}
                      icon={s.icon}
                      color={s.color}
                      onClick={() =>
                        setProductForm((prev) => ({
                          ...prev,
                          ...presetFor(s.key, categories, settings),
                        }))
                      }
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>

            <Card elevation={0} sx={{ borderRadius: 4, border: `1px solid ${BRAND.line}` }}>
              <CardContent>
                <Typography sx={{ fontWeight: 950, color: BRAND.ink, mb: 2 }}>
                  {TT.basicInfo}
                </Typography>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                    gap: 2,
                    minWidth: 0,
                  }}
                >
                  <TextField
                    label={TT.nameTr}
                    value={productForm.name.tr}
                    onChange={(e) =>
                      setProductForm((prev) => ({
                        ...prev,
                        name: { ...prev.name, tr: e.target.value },
                      }))
                    }
                    required
                    fullWidth
                  />

                  <TextField
                    label={TT.nameEn}
                    value={productForm.name.en}
                    onChange={(e) =>
                      setProductForm((prev) => ({
                        ...prev,
                        name: { ...prev.name, en: e.target.value },
                      }))
                    }
                    fullWidth
                  />

                  <TextField
                    label={TT.nameDe}
                    value={productForm.name.de}
                    onChange={(e) =>
                      setProductForm((prev) => ({
                        ...prev,
                        name: { ...prev.name, de: e.target.value },
                      }))
                    }
                    fullWidth
                  />

                  <TextField
                    label={TT.descTr}
                    value={productForm.description.tr}
                    onChange={(e) =>
                      setProductForm((prev) => ({
                        ...prev,
                        description: { ...prev.description, tr: e.target.value },
                      }))
                    }
                    fullWidth
                    multiline
                    minRows={3}
                  />

                  <TextField
                    label={TT.descEn}
                    value={productForm.description.en}
                    onChange={(e) =>
                      setProductForm((prev) => ({
                        ...prev,
                        description: { ...prev.description, en: e.target.value },
                      }))
                    }
                    fullWidth
                    multiline
                    minRows={3}
                  />

                  <TextField
                    label={TT.descDe}
                    value={productForm.description.de}
                    onChange={(e) =>
                      setProductForm((prev) => ({
                        ...prev,
                        description: { ...prev.description, de: e.target.value },
                      }))
                    }
                    fullWidth
                    multiline
                    minRows={3}
                  />
                </Box>
              </CardContent>
            </Card>

            <Card elevation={0} sx={{ borderRadius: 4, border: `1px solid ${BRAND.line}` }}>
              <CardContent>
                <Typography sx={{ fontWeight: 950, color: BRAND.ink, mb: 2 }}>
                  {TT.priceInfo}
                </Typography>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                      md: "repeat(4, minmax(0, 1fr))",
                    },
                    gap: 2,
                    minWidth: 0,
                  }}
                >
                  <FormControl fullWidth>
                    <InputLabel>{TT.category}</InputLabel>
                    <Select
                      label={TT.category}
                      value={productForm.category_id || ""}
                      onChange={(e) =>
                        setProductForm((prev) => ({
                          ...prev,
                          category_id: e.target.value,
                        }))
                      }
                    >
                      {categories.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {localized(c.name, lang)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>{TT.productType}</InputLabel>
                    <Select
                      label={TT.productType}
                      value={productForm.product_type}
                      onChange={(e) =>
                        setProductForm((prev) => ({
                          ...prev,
                          product_type: e.target.value,
                        }))
                      }
                    >
                      {FLOW.productTypes.map((x) => (
                        <MenuItem key={x} value={x}>
                          {productTypeLabel(TT, x)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>{TT.saleType}</InputLabel>
                    <Select
                      label={TT.saleType}
                      value={productForm.sale_type}
                      onChange={(e) =>
                        setProductForm((prev) => ({
                          ...prev,
                          sale_type: e.target.value,
                        }))
                      }
                    >
                      {FLOW.saleTypes.map((x) => (
                        <MenuItem key={x} value={x}>
                          {saleTypeLabel(TT, x)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>{TT.status}</InputLabel>
                    <Select
                      label={TT.status}
                      value={productForm.status}
                      onChange={(e) =>
                        setProductForm((prev) => ({
                          ...prev,
                          status: e.target.value,
                        }))
                      }
                    >
                      {FLOW.statuses.map((x) => (
                        <MenuItem key={x} value={x}>
                          {statusLabel(TT, x)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    label={TT.brand}
                    value={productForm.brand}
                    onChange={(e) =>
                      setProductForm((prev) => ({ ...prev, brand: e.target.value }))
                    }
                    fullWidth
                  />

                  <TextField
                    label={TT.model}
                    value={productForm.model}
                    onChange={(e) =>
                      setProductForm((prev) => ({ ...prev, model: e.target.value }))
                    }
                    fullWidth
                  />

                  <TextField
                    label={TT.sku}
                    value={productForm.sku}
                    onChange={(e) =>
                      setProductForm((prev) => ({ ...prev, sku: e.target.value }))
                    }
                    fullWidth
                  />

                  <FormControl fullWidth>
                    <InputLabel>{TT.currency}</InputLabel>
                    <Select
                      label={TT.currency}
                      value={productForm.currency}
                      onChange={(e) =>
                        setProductForm((prev) => ({
                          ...prev,
                          currency: e.target.value,
                        }))
                      }
                    >
                      {FLOW.currencies.map((c) => (
                        <MenuItem key={c} value={c}>
                          {c}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    label={TT.price}
                    type="number"
                    value={productForm.price}
                    onChange={(e) =>
                      setProductForm((prev) => ({ ...prev, price: e.target.value }))
                    }
                    required
                    fullWidth
                  />

                  <TextField
                    label={TT.oldPrice}
                    type="number"
                    value={productForm.compare_price}
                    onChange={(e) =>
                      setProductForm((prev) => ({
                        ...prev,
                        compare_price: e.target.value,
                      }))
                    }
                    fullWidth
                  />

                  <TextField
                    label={TT.vat}
                    type="number"
                    value={productForm.vat_rate}
                    onChange={(e) =>
                      setProductForm((prev) => ({
                        ...prev,
                        vat_rate: e.target.value,
                      }))
                    }
                    fullWidth
                  />
                </Box>
              </CardContent>
            </Card>

            <ProductImageManager TT={TT} form={productForm} setForm={setProductForm} />

            <Card elevation={0} sx={{ borderRadius: 4, border: `1px solid ${BRAND.line}` }}>
              <CardContent>
                <Typography sx={{ fontWeight: 950, color: BRAND.ink, mb: 2 }}>
                  {TT.stockInfo}
                </Typography>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                    gap: 2,
                    minWidth: 0,
                  }}
                >
                  <TextField
                    label={TT.stockQty}
                    type="number"
                    value={productForm.stock_qty}
                    onChange={(e) =>
                      setProductForm((prev) => ({
                        ...prev,
                        stock_qty: e.target.value,
                      }))
                    }
                    fullWidth
                    disabled={!productForm.stock_enabled}
                  />

                  <TextField
                    label={TT.lowStock}
                    type="number"
                    value={productForm.low_stock}
                    onChange={(e) =>
                      setProductForm((prev) => ({
                        ...prev,
                        low_stock: e.target.value,
                      }))
                    }
                    fullWidth
                    disabled={!productForm.stock_enabled}
                  />

                  <TextField
                    label={TT.sizes}
                    value={productForm.sizesText}
                    onChange={(e) =>
                      setProductForm((prev) => ({
                        ...prev,
                        sizesText: e.target.value,
                      }))
                    }
                    fullWidth
                    helperText="Örn: 36, 37, 38 veya S, M, L"
                  />

                  <TextField
                    label={TT.colors}
                    value={productForm.colorsText}
                    onChange={(e) =>
                      setProductForm((prev) => ({
                        ...prev,
                        colorsText: e.target.value,
                      }))
                    }
                    fullWidth
                    helperText="Örn: Siyah, Beyaz, Pudra"
                  />

                  <TextField
                    label={TT.tags}
                    value={productForm.tagsText}
                    onChange={(e) =>
                      setProductForm((prev) => ({
                        ...prev,
                        tagsText: e.target.value,
                      }))
                    }
                    fullWidth
                    helperText="Örn: Yeni, Kampanya, Hızlı Teslimat"
                  />

                  <TextField
                    label={TT.note}
                    value={productForm.note}
                    onChange={(e) =>
                      setProductForm((prev) => ({
                        ...prev,
                        note: e.target.value,
                      }))
                    }
                    fullWidth
                  />
                </Box>

                <Divider sx={{ my: 2 }} />

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!productForm.stock_enabled}
                        onChange={(e) =>
                          setProductForm((prev) => ({
                            ...prev,
                            stock_enabled: e.target.checked,
                          }))
                        }
                      />
                    }
                    label={TT.stockEnabled}
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!productForm.featured}
                        onChange={(e) =>
                          setProductForm((prev) => ({
                            ...prev,
                            featured: e.target.checked,
                          }))
                        }
                      />
                    }
                    label={TT.featured}
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!productForm.campaign}
                        onChange={(e) =>
                          setProductForm((prev) => ({
                            ...prev,
                            campaign: e.target.checked,
                          }))
                        }
                      />
                    }
                    label={TT.campaign}
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!productForm.new_badge}
                        onChange={(e) =>
                          setProductForm((prev) => ({
                            ...prev,
                            new_badge: e.target.checked,
                          }))
                        }
                      />
                    }
                    label={TT.newBadge}
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!productForm.whatsapp_order}
                        onChange={(e) =>
                          setProductForm((prev) => ({
                            ...prev,
                            whatsapp_order: e.target.checked,
                          }))
                        }
                      />
                    }
                    label={TT.whatsappActive}
                  />
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    icon={<WarehouseIcon />}
                    label={TT.stockModule}
                    color={productForm.integrate_stock ? "success" : "default"}
                    onClick={() =>
                      setProductForm((prev) => ({
                        ...prev,
                        integrate_stock: !prev.integrate_stock,
                      }))
                    }
                    sx={{ fontWeight: 950 }}
                  />

                  <Chip
                    icon={<PointOfSaleIcon />}
                    label={TT.cashModule}
                    color={productForm.integrate_cash ? "success" : "default"}
                    onClick={() =>
                      setProductForm((prev) => ({
                        ...prev,
                        integrate_cash: !prev.integrate_cash,
                      }))
                    }
                    sx={{ fontWeight: 950 }}
                  />

                  <Chip
                    icon={<GroupsIcon />}
                    label={TT.cariModule}
                    color={productForm.integrate_cari ? "success" : "default"}
                    onClick={() =>
                      setProductForm((prev) => ({
                        ...prev,
                        integrate_cari: !prev.integrate_cari,
                      }))
                    }
                    sx={{ fontWeight: 950 }}
                  />

                  <Chip
                    icon={<WhatsAppIcon />}
                    label={TT.whatsappActive}
                    color={productForm.whatsapp_order ? "success" : "default"}
                    onClick={() =>
                      setProductForm((prev) => ({
                        ...prev,
                        whatsapp_order: !prev.whatsapp_order,
                      }))
                    }
                    sx={{ fontWeight: 950 }}
                  />
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </DialogContent>

        <DialogActions className="myp-marketplace-dialog-actions" sx={{ p: 2 }}>
          <Button onClick={() => setProductOpen(false)} sx={outlineButtonSx}>
            {TT.cancel}
          </Button>

          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            sx={primaryButtonSx}
            onClick={saveProduct}
          >
            {productForm.id ? TT.update : TT.save}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        className="myp-marketplace-dialog"
        open={categoryOpen}
        onClose={() => setCategoryOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 950, color: BRAND.ink }}>
          {TT.categoryAdd}
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label={TT.categoryNameTr}
              value={categoryForm.name.tr}
              onChange={(e) =>
                setCategoryForm((prev) => ({
                  ...prev,
                  name: { ...prev.name, tr: e.target.value },
                  slug: slugify(e.target.value),
                }))
              }
              fullWidth
              required
            />

            <TextField
              label={TT.categoryNameEn}
              value={categoryForm.name.en}
              onChange={(e) =>
                setCategoryForm((prev) => ({
                  ...prev,
                  name: { ...prev.name, en: e.target.value },
                }))
              }
              fullWidth
            />

            <TextField
              label={TT.categoryNameDe}
              value={categoryForm.name.de}
              onChange={(e) =>
                setCategoryForm((prev) => ({
                  ...prev,
                  name: { ...prev.name, de: e.target.value },
                }))
              }
              fullWidth
            />

            <TextField
              label={TT.slug}
              value={categoryForm.slug}
              onChange={(e) =>
                setCategoryForm((prev) => ({ ...prev, slug: e.target.value }))
              }
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>{TT.icon}</InputLabel>
              <Select
                label={TT.icon}
                value={categoryForm.icon}
                onChange={(e) =>
                  setCategoryForm((prev) => ({ ...prev, icon: e.target.value }))
                }
              >
                <MenuItem value="category">Genel</MenuItem>
                <MenuItem value="fashion">Butik / Giyim</MenuItem>
                <MenuItem value="car">Araç</MenuItem>
                <MenuItem value="home">Emlak</MenuItem>
                <MenuItem value="computer">Elektronik</MenuItem>
                <MenuItem value="cosmetic">Kozmetik</MenuItem>
                <MenuItem value="food">Gıda</MenuItem>
                <MenuItem value="service">Hizmet</MenuItem>
                <MenuItem value="digital">Dijital</MenuItem>
                <MenuItem value="other">Diğer</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>{TT.color}</InputLabel>
              <Select
                label={TT.color}
                value={categoryForm.color}
                onChange={(e) =>
                  setCategoryForm((prev) => ({ ...prev, color: e.target.value }))
                }
              >
                <MenuItem value={BRAND.orange}>Turuncu</MenuItem>
                <MenuItem value={BRAND.blue}>Mavi</MenuItem>
                <MenuItem value={BRAND.green}>Yeşil</MenuItem>
                <MenuItem value={BRAND.pink}>Pembe</MenuItem>
                <MenuItem value={BRAND.violet}>Mor</MenuItem>
                <MenuItem value={BRAND.dark}>Siyah</MenuItem>
                <MenuItem value={BRAND.muted}>Gri</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={categoryForm.active !== false}
                  onChange={(e) =>
                    setCategoryForm((prev) => ({ ...prev, active: e.target.checked }))
                  }
                />
              }
              label={TT.active}
            />
          </Stack>
        </DialogContent>

        <DialogActions className="myp-marketplace-dialog-actions" sx={{ p: 2 }}>
          <Button onClick={() => setCategoryOpen(false)} sx={outlineButtonSx}>
            {TT.cancel}
          </Button>

          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            sx={primaryButtonSx}
            onClick={saveCategory}
          >
            {TT.save}
          </Button>
        </DialogActions>
      </Dialog>

      <ProductPreviewDialog
        TT={TT}
        open={!!previewProduct}
        onClose={() => setPreviewProduct(null)}
        product={previewProduct}
        category={previewProduct ? categoryMap.get(String(previewProduct.category_id)) : null}
        lang={lang}
        onEdit={(p) => {
          setPreviewProduct(null);
          openEditProduct(p);
        }}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={4200}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
          sx={{ fontWeight: 850, borderRadius: 3 }}
        >
          {snack.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}
